import { useState, useRef, useEffect, useMemo, Fragment } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, RotateCcw, Printer, Share2, Clock, MapPin, Coffee, TrainFront, Footprints, GripVertical, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Instagram, Map as MapIcon, X, AlertTriangle, Navigation, Check, QrCode, CornerDownRight, Plus, Info, Luggage, SkipForward, Ticket, Lock, Unlock, CalendarDays, Utensils } from "lucide-react";
import { Mark } from "@/components/Mark";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./motion";
import { DayMiniMap } from "./DayMiniMap";
import { buildTripMapImage, drawMiniMap, drawLightsString, drawSnowman, drawPresent, drawReindeer } from "../lib/tripMap";
import { useActiveAds } from "../lib/ads";
import {
  SPOTS,
  CLUSTERS,
  MONTHS,
  MONTH_INFO,
  MONTH_EVENTS,
  MONTH_TIPS,
  isAvailable,
  seasonHint,
  spotCat,
  catRank,
  CAT_LABELS,
  openingNotes,
  generatePlan,
  generateBalancedPlan,
  extendCuratedPlan,
  planFromDays,
  restoreSavedPlan,
  floatTivoli,
  pickInsertDay,
  insertionIndexFor,
  edtInsertIndex,
  ITINERARY_3D,
  ITINERARY_ALL,
  spotById,
  registerCustomSpot,
  buildDayFromItems,
  fmtHours,
  spotDur,
  travelBetween,
  dayTripTransit,
  rebuildDayOrder,
  spotImg,
  AREA_AFFINITY,
  areaKey,
  DAY_MAX,
  companionsOf,
  XMAS_WALK,
  BAKERY_WALK,
  setTripContext,
  closureReason,
  isOpenOnDate,
  nextOpeningDate,
  seasonLabel,
  fitsBeforeClose,
} from "../data/planner";
import { deriveTrip, fitPlanToTrip, fmtDate, fmtTime, MAX_PLAN_DAYS } from "../data/plannerDates";
import { TripDates } from "./planner/TripDates";
import { HotelInput } from "./planner/HotelInput";
import { PickerToggles } from "./planner/PickerToggles";
import { MealRow, HotelLeg } from "./planner/MealRow";
import { ArrivalEveningCard, DepartureMorningCard } from "./planner/EdgeDayCards";
import { BookAhead, bookableStops, BOOK_AHEAD } from "./planner/BookAhead";

const CLUSTER_IDS = Object.keys(CLUSTERS);

// Tailored companion suggestions: a few pairs get a specific "why add this"
// note instead of the generic "pairs perfectly" line. Keyed `addedId>companionId`.
const COMPANION_NOTES = {
  "juno>bosses": {
    title: "Add Bosses Bager — the old-school alternative",
    body: "A classic traditional bakery a few minutes away. Great for tasting modern Juno against an old-school one — and a smart backup if Juno's queue is too long. (No cardamom bun is worth a 1-hour wait.)",
  },
};

// "45 min" / "1h 20m" — for a day's on-foot total on the day card
const fmtWalkMin = (m) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`);

// selection is "itinerary-anchored" when most of the suggested itinerary is
// in it — Build then extends the locked days instead of re-shuffling them
const CURATED_IDS = new Set(ITINERARY_ALL.flat());

// A day ending at Tivoli is a normal day plus a Tivoli evening — its hours
// are evening hours (open late), so cards show daytime + evening separately
// and the busy-day badge judges the daytime only
// Tivoli secretly counts as 3h when packing (see effDur in planner.js) — the
// daytime total subtracts those 3h, not the card's 4h, so evenings stay capped.
const TIVOLI_PACK_H = 3;
const endsWithTivoli = (d) => d.items.length > 0 && d.items[d.items.length - 1].id === "tivoli";
const daytimeHours = (d) => (endsWithTivoli(d) ? Math.max(0, d.hours - TIVOLI_PACK_H) : d.hours);

const SAVE_KEY = "cph_plan_v2";
const DISMISS_KEY = "cph_plan_hidden_v1";
const SUGGEST_KEY = "cph_plan_suggest_hidden_v1";
const BAKERY_TOGGLE_KEY = "cph_plan_show_bakeries_v1";
const PICKER_TOGGLES_KEY = "cph_plan_picker_show_v1";
const TRIP_KEY = "cph_plan_trip_v2";
const HOTEL_KEY = "cph_plan_hotel_v2";
// the city centre for the "Outside the centre" picker toggle
const CENTRE_CLUSTERS = new Set(["indre-by", "harbour", "daytrip"]);
const EMPTY_TRIP = { start: null, end: null, arriveFrom: null, leaveBy: null };
const tripDatesReady = (t) => !!(t.start && t.end);
const readJSON = (key, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || "null");
    return v ?? fallback;
  } catch {
    return fallback;
  }
};

// Ideas offered on a free day — whole-day outings vs a couple of hours.
// Anything already in the visitor's plan is filtered out at render time.
const FREE_DAY_IDEAS = {
  whole: ["zoo", "tivoli", "louisiana", "roskilde", "helsingor"],
  hours: ["designmuseum", "thorvaldsens", "glyptoteket", "rosenborg"],
};

// Small nearby add-ons offered on a Tivoli-only day (you eat inside the gardens,
// so no lunch break) — the day stays a "Tivoli day" while it's just these extras.
const TIVOLI_DAY_EXTRAS = ["istedgade", "ismageriet-kodbyen", "buka"];

// Area-completion cards: when a built day is basically ONE neighbourhood (2–4
// stops, at most one outside it) but missing the area's signature stop, suggest
// it — an all-Østerbro day without a bakery gets Juno, an all-Nørrebro day
// without Jægersborggade gets the street, and so on. kind:"bakery" skips the
// card when the day already has any bakery/crawl stop. Harbour days are
// excluded by design; Indre By instead offers its 2 nearest unselected stops
// when the centre day is down at 2–3 stops ("make a day of it").
const AREA_SIGNATURES = {
  osterbro: { label: "Østerbro", kind: "bakery", ids: ["juno", "bosses"], text: "An Østerbro day without a bakery stop? Juno's cardamom bun is the area's claim to fame — it slots right in." },
  norrebro: { label: "Nørrebro", ids: ["jaegersborggade"], text: "The missing piece: Jægersborggade — the little maker-and-wine-bar street that is Nørrebro." },
  vesterbro: { label: "Vesterbro", ids: ["koedbyen"], text: "No Vesterbro day is complete without Kødbyen — the Meatpacking District's food and bars." },
  christianshavn: { label: "Christianshavn", ids: ["christiania"], text: "You're one stop short: Freetown Christiania is Christianshavn's headline." },
  refshaleoen: { label: "Refshaleøen", ids: ["reffen", "cc"], text: "Round the day out with Reffen's street-food yard — or Copenhagen Contemporary when Reffen is off-season." },
  "islands-brygge": { label: "Islands Brygge", ids: ["harbourbath", "andersen"], text: "The missing Bryggen ritual: the harbour bath in summer — Andersen Bakery's waterfront table off-season." },
  amager: { label: "Amager", kind: "bakery", ids: ["eriks", "alice"], text: "Add a proper old-school bakery — Eriks on Amagerbrogade is the Amager classic." },
  frederiksberg: { label: "Frederiksberg", ids: ["frederiksberghave"], text: "The missing breather: Frederiksberg Have — the city's prettiest park is right there." },
  nordvest: { label: "Nordvest", ids: ["grundtvigs"], text: "The missing masterpiece: Grundtvigs Kirke — Nordvest's brick cathedral." },
};
const API = process.env.REACT_APP_BACKEND_URL;

// Legacy season ids → a representative month (keeps old shared links working)
const LEGACY_SEASON_MONTH = { spring: 4, summer: 7, autumn: 9, winter: 12 };

export const Planner = ({ initialTrip = null } = {}) => {
  const [month, setMonth] = useState("any");
  const [days, setDays] = useState(3);
  const [daysAuto, setDaysAuto] = useState(false); // "not sure" mode — we work out the day count
  const [pace, setPace] = useState("relaxed"); // auto-pack pace: relaxed = standard ~8.5h days, packed = fuller ~10h days
  const [selected, setSelected] = useState([]);
  const [customSpots, setCustomSpots] = useState([]); // visitor's own stops, from pasted Google Maps links
  const [customLink, setCustomLink] = useState("");
  const [customBusy, setCustomBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((id) => SPOTS.some((s) => s.id === id)) : [];
    } catch {
      return [];
    }
  });
  const [plan, setPlan] = useState(null);
  const [warning, setWarning] = useState(null);
  const [tipsDismissed, setTipsDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(SUGGEST_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [itemDrag, setItemDrag] = useState(null); // { day: dayIdx, idx: itemIdx } while dragging a stop
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const shareCacheRef = useRef({ sig: null, url: null });
  const restoredRef = useRef(false);
  const outputRef = useRef(null);

  // November/December trips get the Christmas treatment on the shareables —
  // festive mini-maps, trip map image and Story image (the page itself and
  // the print/PDF stay classic).
  const festive = Number(month) === 11 || Number(month) === 12;

  // Picker filters: crawl bakery stops start hidden (there are a lot of
  // them); museums and everything outside the centre start visible. Remembered
  // on this device. The one-tap bakery-crawl fill doesn't depend on visibility.
  const [pickerShow, setPickerShow] = useState(() => {
    const saved = readJSON(PICKER_TOGGLES_KEY, null);
    if (saved) return { bakeries: !!saved.bakeries, museums: saved.museums !== false, outside: saved.outside !== false };
    let bakeries = false;
    try {
      bakeries = localStorage.getItem(BAKERY_TOGGLE_KEY) === "1";
    } catch {}
    return { bakeries, museums: true, outside: true };
  });
  const showBakeries = pickerShow.bakeries;

  useEffect(() => {
    try {
      localStorage.setItem(PICKER_TOGGLES_KEY, JSON.stringify(pickerShow));
    } catch {}
  }, [pickerShow]);

  // Exact dates + times and the hotel (all optional). Derived: which
  // calendar days are sightseeing days, the month, first/last-day limits.
  const [tripDates, setTripDates] = useState(() => ({ ...EMPTY_TRIP, ...readJSON(TRIP_KEY, {}) }));
  const [hotel, setHotel] = useState(() => readJSON(HOTEL_KEY, null));
  // "I don't know my dates yet" → reveals the month + day-count pickers
  const [noDates, setNoDates] = useState(() => readJSON(TRIP_KEY, {}).unknown === true);
  const whenChosen = !!tripDatesReady(tripDates) || noDates;
  const trip = useMemo(() => deriveTrip(tripDates), [tripDates]);
  const tripValid = trip && !trip.invalid ? trip : null;
  // the calendar dates of the plan days (empty without dates)
  const planDates = useMemo(() => {
    if (!tripValid) return [];
    return Array.from({ length: Math.min(tripValid.planDays, MAX_PLAN_DAYS) }, (_, i) => {
      const d = new Date(tripValid.planStart);
      d.setDate(d.getDate() + i);
      return d;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripValid ? +tripValid.planStart : 0, tripValid?.planDays]);
  // Is a spot worth offering for this trip? With exact dates: open on at least
  // one of the plan days (real seasons — Tivoli's October gap, Reffen's
  // mid-March start…); otherwise the month-based check.
  const avail = (s) => (s ? (planDates.length ? planDates.some((dt) => isOpenOnDate(s, dt)) : isAvailable(s, month)) : false);
  useEffect(() => {
    try {
      if (tripDates.start || tripDates.end || tripDates.arriveFrom != null || tripDates.leaveBy != null || noDates)
        localStorage.setItem(TRIP_KEY, JSON.stringify({ ...tripDates, unknown: noDates || undefined }));
      else localStorage.removeItem(TRIP_KEY);
      if (hotel) localStorage.setItem(HOTEL_KEY, JSON.stringify(hotel));
      else localStorage.removeItem(HOTEL_KEY);
    } catch {}
  }, [tripDates, hotel, noDates]);

  // Fit a freshly built plan to the trip (dates → closing days, short first /
  // last day). Without dates this is a no-op.
  const finalize = (p) => {
    if (!p || !tripValid) {
      return p;
    }
    const { plan: fitted, swapOut, added } = fitPlanToTrip(p, month);
    if (swapOut.length || added.length)
      setSelected((sel) => [...sel.filter((id) => !swapOut.includes(id)), ...added.filter((id) => !sel.includes(id))]);
    return fitted;
  };

  // The trip context the day builders read (dates, times, hotel). Whenever
  // it changes, every day card is refreshed and the plan re-fitted.
  const ctxSig = JSON.stringify({
    ps: tripValid ? +tripValid.planStart : null,
    pd: tripValid ? tripValid.planDays : 0,
    ff: tripValid?.firstFrom ?? null,
    lb: tripValid?.lastBy ?? null,
    h: hotel,
  });
  const applyTripContext = () =>
    setTripContext({
      planStart: tripValid?.planStart ?? null,
      planDays: tripValid ? Math.min(tripValid.planDays, MAX_PLAN_DAYS) : 0,
      firstFrom: tripValid?.firstFrom ?? null,
      lastBy: tripValid?.lastBy ?? null,
      hotel,
    });
  // keep the module-level context in step with this render (cheap, idempotent —
  // guards against anything re-initialising the planner module underneath us)
  applyTripContext();
  useEffect(() => {
    applyTripContext();
    setPlan((prev) => {
      if (!prev) return prev;
      const refreshed = prev.map((d) => rebuildDayOrder(d.items, d, month));
      if (!tripValid) {
        return refreshed;
      }
      const { plan: fitted, swapOut, added } = fitPlanToTrip(refreshed, month);
      if (swapOut.length || added.length)
        setSelected((sel) => [...sel.filter((id) => !swapOut.includes(id)), ...added.filter((id) => !sel.includes(id))]);
      return fitted;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxSig]);

  // --- Locked days -----------------------------------------------------------
  // A locked day is final: re-builds, re-packs, day-count changes and auto-adds
  // all work around it — it keeps its stops, their order and its slot.
  const toggleLock = (idx) => {
    setPlan((prev) => prev.map((d, i) => (i === idx ? { ...d, locked: !d.locked } : d)));
    const now = !plan?.[idx]?.locked;
    toast.success(now ? `Day ${idx + 1} locked — re-builds leave it exactly as it is.` : `Day ${idx + 1} unlocked.`);
  };
  const withLocks =
    (gen) =>
    (ids, nDays, m, ...rest) => {
      const locked = (plan || []).filter((d) => d.locked);
      if (!locked.length) return gen(ids, nDays, m, ...rest);
      const lockedIds = new Set(locked.flatMap((d) => d.items.map((s) => s.id)));
      const freeIds = ids.filter((id) => !lockedIds.has(id));
      const freeDays = nDays - locked.length;
      const free = freeDays > 0 ? gen(freeIds, freeDays, m, ...rest) : freeIds.length ? [buildDayFromItems(freeIds.map(spotById).filter(Boolean), 1, m)] : [];
      const out = [];
      let fi = 0;
      const total = Math.max(nDays, locked.length + free.length);
      for (let i = 0; i < total; i++) {
        const l = locked.find((d) => d.day === i + 1);
        out.push(l || free[fi++]);
      }
      return out.filter(Boolean).map((d, i) => (d.day === i + 1 ? d : rebuildDayOrder(d.items, { ...d, day: i + 1 }, m)));
    };
  const genPlan = withLocks(generatePlan);
  const genCurated = withLocks(extendCuratedPlan);
  const genBalanced = withLocks(generateBalancedPlan);

  // Subscribe to active advertiser listings — this both registers tier 2+ ads
  // into SPOTS (via lib/ads) and re-renders the planner once they arrive so the
  // "Local partners" group shows even on a hard page load.
  const activeAds = useActiveAds();
  const spotGroups = useMemo(
    () =>
      CLUSTER_IDS.map((cid) => ({
        cid,
        items: SPOTS.filter(
          (s) =>
            s.cluster === cid &&
            !dismissed.includes(s.id) &&
            !(s.hideOffSeason && !avail(s)) &&
            (showBakeries || !s.crawl) &&
            (pickerShow.museums || spotCat(s.id) !== "museum") &&
            (pickerShow.outside || CENTRE_CLUSTERS.has(s.cluster))
        )// group by category inside each area (strolls together, food together…)
          .sort((a, b) => catRank(a.id) - catRank(b.id)),
      })).filter((g) => g.items.length),
    [month, activeAds, dismissed, pickerShow, showBakeries]
  );

  // Exact dates drive the month and the day count. A change in day count
  // re-spreads an on-screen plan the same way the day buttons do.
  const tripDaysSig = tripValid ? `${tripValid.month}-${tripValid.planDays}` : null;
  useEffect(() => {
    if (!tripValid) return;
    const nd = Math.min(tripValid.planDays, MAX_PLAN_DAYS);
    setDaysAuto(false);
    setWarning(null);
    if (tripValid.month !== month) {
      setMonth(tripValid.month);
      setSelected((sel) => sel.filter((id) => avail(spotById(id))));
    }
    if (nd !== days) {
      setDays(nd);
      if (plan) {
        if (nd > plan.length) {
          const kept = plan.map((d) => rebuildDayOrder(d.items, d, tripValid.month));
          setPlan(finalize([...kept, ...Array.from({ length: nd - plan.length }, (_, k) => buildDayFromItems([], plan.length + 1 + k, tripValid.month))]));
        } else {
          const ids = plan.flatMap((d) => d.items.map((s) => s.id));
          const next = genPlan(ids, nd, tripValid.month);
          if (next.some((x) => x.hours > 12))
            setWarning(`${nd} day${nd > 1 ? "s" : ""} is too few for everything you picked — a day would run past 12 hours. Remove a few spots.`);
          else setPlan(finalize(next));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDaysSig]);

  // One-time housekeeping: after three days of planner/EDT updates, drop any
  // pre-v2 saved plan, dates and hotel so returning visitors start completely
  // fresh instead of having a stale plan restored (storage keys were bumped
  // to v2). Runs once; harmless if the old keys aren't there.
  useEffect(() => {
    try {
      ["cph_plan_v1", "cph_plan_trip_v1", "cph_plan_hotel_v1"].forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      /* ignore */
    }
  }, []);

  // On mount, load a plan by priority: a plan opened via /plan/:code (initialTrip)
  // > a legacy ?trip= URL > the visitor's own auto-saved plan on this device.
  useEffect(() => {
    // groups = an explicit day-by-day arrangement ([[ids day 1], [ids day 2], …]).
    // When present, the days AND the order of stops are kept exactly — the
    // planner must never re-cluster a plan that was already arranged.
    const apply = (m, nd, spotIds, { scroll = false, restored = false, groups = null, locked = [], lunch = [], dinner = [], edt = false, edtOffset = 0 } = {}) => {
      setMonth(m);
      setDays(groups ? groups.length : nd);
      setSelected(spotIds);
      const built = groups ? (restored ? restoreSavedPlan(groups, m) : planFromDays(groups, m)) : generatePlan(spotIds, nd, m);
      setPlan(
        finalize(
          built.map((d, i) => {
            const extra = {
              ...(locked.includes(i) ? { locked: true } : {}),
              ...(Number.isInteger(lunch[i]) ? { lunchAfter: lunch[i] } : {}),
              ...(dinner[i] && dinner[i].name ? { dinnerPick: dinner[i] } : {}),
              ...(edt ? { edt: true, edtOffset } : {}),
            };
            return Object.keys(extra).length ? rebuildDayOrder(d.items, { ...d, ...extra }, m) : d;
          })
        )
      );
      if (restored) {
        restoredRef.current = true;
        toast.success("Welcome back — we restored your saved plan.", {
          action: { label: "Start fresh", onClick: () => { setPlan(null); setSelected([]); setWarning(null); } },
        });
      }
      if (scroll) {
        setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 700);
      }
    };

    const validIds = (ids) =>
      Array.from(new Set(ids)).filter((id) => !!spotById(id));

    // A stored/passed day arrangement is only trusted when it covers exactly
    // the same spots as the flat list — otherwise fall back to clustering.
    const saneGroups = (groups, spotIds) => {
      if (!Array.isArray(groups) || !groups.length) return null;
      const cleaned = groups
        .slice(0, MAX_PLAN_DAYS)
        .map((g) => (Array.isArray(g) ? g.filter((id) => spotIds.includes(id)) : []));
      const flat = cleaned.flat();
      if (flat.length !== spotIds.length || !spotIds.every((id) => flat.includes(id))) return null;
      return cleaned;
    };

    const monthOf = (raw) => {
      const asNum = Number(raw);
      return asNum >= 1 && asNum <= 12 ? asNum : LEGACY_SEASON_MONTH[raw] || "any";
    };

    const parseTrip = (trip) => {
      const [s, d, ids] = (trip || "").split("~");
      // day-grouped form: "day1id,day1id;day2id;…" — each ; group is one day,
      // order inside each day kept exactly
      if ((ids || "").includes(";")) {
        const groups = ids.split(";").slice(0, MAX_PLAN_DAYS).map((g) => validIds(g.split(",")));
        const spotIds = Array.from(new Set(groups.flat()));
        if (!spotIds.length) return null;
        return { m: monthOf(s), nd: groups.length, spotIds, groups };
      }
      const spotIds = validIds((ids || "").split(","));
      if (!spotIds.length) return null;
      const nd = Number(d) >= 1 && Number(d) <= MAX_PLAN_DAYS ? Number(d) : 3;
      // single-day links (the layover plan) keep the link's stop order exactly —
      // the author arranged it on purpose ("skip everything after the boat"),
      // and re-clustering one day gains nothing anyway
      return { m: monthOf(s), nd, spotIds, groups: nd === 1 ? [spotIds] : null };
    };

    // 1) explicit plan from /plan/:code
    if (initialTrip && Array.isArray(initialTrip.spots) && initialTrip.spots.length) {
      // a shared plan can carry the creator's own pasted-link stops
      if (Array.isArray(initialTrip.custom_spots)) {
        initialTrip.custom_spots.forEach((c) => registerCustomSpot(c));
        if (initialTrip.custom_spots.length) setCustomSpots(initialTrip.custom_spots);
      }
      const spotIds = validIds(initialTrip.spots);
      if (spotIds.length) {
        const m = monthOf(initialTrip.month);
        const nd = Number(initialTrip.days) >= 1 && Number(initialTrip.days) <= MAX_PLAN_DAYS ? Number(initialTrip.days) : 3;
        // a shared plan carries its dates + hotel too
        if (initialTrip.trip && typeof initialTrip.trip === "object") {
          const t = initialTrip.trip;
          if (t.start && t.end) setTripDates({ start: t.start, end: t.end, arriveFrom: t.arriveFrom ?? null, leaveBy: t.leaveBy ?? null });
          if (t.hotel && Array.isArray(t.hotel.coords)) setHotel({ name: t.hotel.name, coords: t.hotel.coords });
        }
        apply(m, nd, spotIds, { scroll: true, groups: saneGroups(initialTrip.day_spots, spotIds) });
        return;
      }
    }

    // 2) legacy ?trip= link
    const urlTrip = new URLSearchParams(window.location.search).get("trip");
    if (urlTrip) {
      const parsed = parseTrip(urlTrip);
      if (parsed) {
        const params = new URLSearchParams(window.location.search);
        const edt = params.get("edt") === "1";
        const readyStr = params.get("ready");
        const edtOffset = edt && readyStr && /^\d{1,2}:\d{2}$/.test(readyStr)
          ? (() => { const [h, m] = readyStr.split(":").map(Number); return h * 60 + m - 570; })()
          : 0;
        apply(parsed.m, parsed.nd, parsed.spotIds, { scroll: true, groups: parsed.groups, edt, edtOffset });
        return;
      }
    }

    // 3) ?month=N deep link (from the month guides) — preselects the travel month only;
    //    a saved or shared plan below still wins if there is one
    const urlMonth = Number(new URLSearchParams(window.location.search).get("month"));
    if (urlMonth >= 1 && urlMonth <= 12) setMonth(urlMonth);

    // 4) the visitor's own auto-saved plan
    try {
      const saved = JSON.parse(localStorage.getItem(SAVE_KEY) || "null");
      if (saved && Array.isArray(saved.selected)) {
        // re-register the visitor's own stops before validating ids
        if (Array.isArray(saved.custom)) {
          saved.custom.forEach((c) => registerCustomSpot(c));
          if (saved.custom.length) setCustomSpots(saved.custom);
        }
        const spotIds = validIds(saved.selected);
        if (spotIds.length) {
          const m = saved.month === "any" ? "any" : Number(saved.month) || "any";
          const nd = Number(saved.days) >= 1 && Number(saved.days) <= MAX_PLAN_DAYS ? Number(saved.days) : 3;
          apply(m, nd, spotIds, {
            restored: true,
            groups: saneGroups(saved.daySpots, spotIds),
            locked: Array.isArray(saved.lockedDays) ? saved.lockedDays : [],
            lunch: Array.isArray(saved.lunchAfter) ? saved.lunchAfter : [],
            dinner: Array.isArray(saved.dinnerPick) ? saved.dinnerPick : [],
          });
        }
      }
    } catch (e) {
      /* ignore corrupt storage */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTrip]);

  // Auto-save the current selection so returning visitors get their plan back.
  // The day-by-day arrangement (incl. any manual reordering) is saved with it,
  // so a restored plan looks exactly the way the visitor left it.
  useEffect(() => {
    try {
      if (selected.length) {
        localStorage.setItem(
          SAVE_KEY,
          JSON.stringify({
            month,
            days,
            selected,
            daySpots: plan ? plan.map((d) => d.items.map((s) => s.id)) : null,
            lockedDays: plan ? plan.map((d, i) => (d.locked ? i : -1)).filter((i) => i >= 0) : undefined,
            lunchAfter: plan && plan.some((d) => Number.isInteger(d.lunchAfter)) ? plan.map((d) => (Number.isInteger(d.lunchAfter) ? d.lunchAfter : null)) : undefined,
            dinnerPick: plan && plan.some((d) => d.dinnerPick) ? plan.map((d) => d.dinnerPick || null) : undefined,
            custom: customSpots.length ? customSpots : undefined,
          })
        );
      } else {
        localStorage.removeItem(SAVE_KEY);
      }
    } catch (e) {
      /* storage may be unavailable (private mode) */
    }
  }, [month, days, selected, plan, customSpots]);

  const toggle = (id) => {
    setWarning(null);
    if (selected.includes(id)) {
      // removing a stop: only its own day is re-flowed — every other day keeps
      // the visitor's exact arrangement
      setSelected((s) => s.filter((x) => x !== id));
      setPlan((prev) =>
        prev
          ? prev.map((d) =>
              d.items.some((x) => x.id === id)
                ? rebuildDayOrder(d.items.filter((x) => x.id !== id), d, month)
                : d
            )
          : prev
      );
      return;
    }
    // adding a stop: offer its companion as a one-tap add (e.g. Home of
    // Carlsberg → the Carlsberg Byen stroll) — unless it's already picked or
    // the visitor deliberately hid it
    const comp = companionsOf(id).find((c) => !selected.includes(c) && !dismissed.includes(c));
    if (comp) {
      const cs = spotById(comp);
      if (cs && avail(cs)) {
        // some pairs get a tailored "why" note; the rest use the generic line
        const note = COMPANION_NOTES[`${id}>${comp}`];
        if (note) {
          toast(note.title, {
            description: note.body,
            duration: 9000,
            action: { label: "+ Add", onClick: () => toggle(comp) },
          });
        } else {
          toast(`Pairs perfectly with that — ${cs.name} (${spotDur(cs)})`, {
            action: { label: "+ Add", onClick: () => toggle(comp) },
          });
        }
      }
    }
    // adding a stop while a plan is on screen: it joins the roomiest sensible
    // day (addSuggested) instead of re-clustering the whole plan
    if (plan) {
      // in "not sure" mode the day count is ours — re-pack when picks change
      if (daysAuto) {
        const ids = [...selected, id];
        const auto = autoDaysFor(ids, month);
        setSelected(ids);
        if (auto) {
          setDays(auto.days);
          setPlan(finalize(auto.plan));
          if (auto.days !== plan.length)
            toast.success(`That's ${auto.days} days of stuff now — re-packed for you.`);
        } else {
          setPlan(null);
          setWarning("That won't fit in 12 days — remove a few spots.");
        }
        return;
      }
      addSuggested(id);
      return;
    }
    setSelected((s) => (s.includes(id) ? s : [...s, id]));
  };

  // persist the visitor's hidden/dismissed spots so their pared-down overview sticks
  useEffect(() => {
    try {
      if (dismissed.length) localStorage.setItem(DISMISS_KEY, JSON.stringify(dismissed));
      else localStorage.removeItem(DISMISS_KEY);
    } catch (e) {
      /* storage may be unavailable (private mode) */
    }
  }, [dismissed]);

  const dismissSpot = (id) => {
    setSelected((sel) => sel.filter((x) => x !== id));
    setDismissed((d) => (d.includes(id) ? d : [...d, id]));
  };

  const restoreHidden = () => setDismissed([]);

  // Local's-tip cards for the built plan (breakfast ideas per day theme) — tip
  // cards only, never auto-added, each dismissible for good.
  const dismissTip = (id) => {
    setTipsDismissed((d) => {
      const next = d.includes(id) ? d : [...d, id];
      try {
        localStorage.setItem(SUGGEST_KEY, JSON.stringify(next));
      } catch (e) {
        /* storage may be unavailable */
      }
      return next;
    });
  };
  // Lunch / dinner rows are dismissible like the tip cards — per day, or for
  // every day via the toast action. Remembered on this device (SUGGEST_KEY).
  const mealShown = (kind, d) => !tipsDismissed.includes(`meal-${kind}-all`) && !tipsDismissed.includes(`meal-${kind}-day${d.day}`);
  const dismissMeal = (kind, d) => {
    dismissTip(`meal-${kind}-day${d.day}`);
    toast(`${kind === "lunch" ? "Lunch break" : "Dinner idea"} hidden on day ${d.day}.`, {
      action: { label: "Hide on every day", onClick: () => dismissTip(`meal-${kind}-all`) },
    });
  };
  // Bring a hidden lunch / dinner suggestion back — clears both the per-day and
  // the "every day" flags so a one-tap dismissal is never a dead end.
  const restoreMeal = (kind, d) => {
    setTipsDismissed((prev) => {
      const next = prev.filter((x) => x !== `meal-${kind}-all` && x !== `meal-${kind}-day${d.day}`);
      try {
        localStorage.setItem(SUGGEST_KEY, JSON.stringify(next));
      } catch (e) {
        /* storage may be unavailable */
      }
      return next;
    });
  };
  // Move the lunch break one stop earlier / later on a day — the position is
  // pinned on the day (survives re-flows) and the clock re-runs around it.
  const moveLunch = (di, delta) => {
    setPlan((prev) =>
      prev.map((d, i) => {
        if (i !== di || !d.timeline?.lunch) return d;
        const next = Math.max(0, Math.min(d.items.length - 1, d.timeline.lunch.afterIdx + delta));
        return rebuildDayOrder(d.items, { ...d, lunchAfter: next }, month);
      })
    );
  };
  // Book a dinner venue into a day (~1½h after the last stop) or take it out.
  const setDinner = (di, venue) => {
    setPlan((prev) =>
      prev.map((d, i) =>
        i === di
          ? rebuildDayOrder(d.items, { ...d, dinnerPick: venue ? { name: venue.name, tag: venue.tag, coords: venue.coords, min: venue.min } : null }, month)
          : d
      )
    );
    if (venue) toast.success(`Dinner at ${venue.name} added to day ${di + 1} — ~1½h on the clock.`);
  };
  // Add a suggested stop WITHOUT touching the rest of the plan: it joins the
  // day the tip belongs to (or the roomiest sensible day) and only that day is
  // re-flowed — every other day keeps the visitor's exact arrangement.
  const addSuggested = (id, dayIdx = null) => {
    if (selected.includes(id)) return;
    const spot = spotById(id);
    if (!spot) return;
    setSelected((sel) => [...sel, id]);
    setPlan((prev) => {
      if (!prev) return generatePlan([...selected, id], days, month);
      // locked days only take a stop the visitor adds to that day on purpose
      let target = dayIdx != null && prev[dayIdx] ? dayIdx : pickInsertDay(prev, spot, (d) => !d.locked);
      const anyLocked = prev.some((d) => d.locked);
      const place = (p) => (anyLocked ? p : floatTivoli(p, month));
      // every day is locked: the newcomer opens a fresh day at the end
      if (target < 0) {
        setDays(prev.length + 1);
        return [...prev, buildDayFromItems([spot], prev.length + 1, month)];
      }
      return place(
        prev.map((d, i) => {
          if (i !== target) return d;
          // an EDT day keeps its fixed timetable order — the newcomer slots
          // into its canonical EDT position (no geographic re-clustering) and
          // the clock recomputes around it
          if (d.edt) {
            const items = d.items.slice();
            items.splice(edtInsertIndex(items, spot), 0, spot);
            return rebuildDayOrder(items, d, month);
          }
          // a loaded plan is never re-sorted — the newcomer slots in by the
          // shared insertion rules (nearest stop, never inside an adjacent
          // pair, GoBoat last when the day has none of its neighbours) and
          // everything else stays exactly as is
          const items = d.items.slice();
          items.splice(insertionIndexFor(items, spot), 0, spot);
          return rebuildDayOrder(items, d, month, { reflow: true });
        })
      );
    });
  };
  // EDT only: the time the visitor is ready in the city. The first stop
  // (Amagergade) sits 15 min later to leave room for a quick breakfast, so
  // ready 09:30 → Amagergade 09:45 by default. Shifts the whole one-day
  // timetable by the difference from that 09:30 start.
  const edtReadyStr = (d) => {
    const mins = 570 + (d.edtOffset || 0);
    return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  };
  const setEdtReady = (dayIdx, timeStr) => {
    if (!/^\d{1,2}:\d{2}$/.test(timeStr)) return;
    const [h, m] = timeStr.split(":").map(Number);
    const offset = h * 60 + m - 570;
    setPlan((prev) => (prev ? prev.map((d, i) => (i === dayIdx ? rebuildDayOrder(d.items, { ...d, edtOffset: offset }, month) : d)) : prev));
  };
  // Paste a Google Maps link → the backend resolves it to a name + coordinates
  // (deterministic parsing, no AI) → it becomes a normal stop: pinned on the
  // map, walking times, draggable, and it rides along in saved/shared plans.
  const addCustomStop = async () => {
    const link = customLink.trim();
    if (!link || customBusy) return;
    setCustomBusy(true);
    try {
      const { data } = await axios.get(`${API}/api/planner/resolve-place`, { params: { url: link } });
      const id = `custom-${Date.now().toString(36)}`;
      const spot = { id, name: data.name, coords: [data.lat, data.lng] };
      registerCustomSpot(spot);
      setCustomSpots((prev) => [...prev, spot]);
      setCustomLink("");
      if (plan) {
        addSuggested(id); // joins the day it fits — same flow as the idea pills
        toast.success(`${data.name} added to your plan.`);
      } else {
        setSelected((sel) => [...sel, id]);
        toast.success(`${data.name} added — build your trip to place it.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read that link — copy it from the place's own Google Maps page.");
    } finally {
      setCustomBusy(false);
    }
  };
  const marketHasSmorrebrod = selected.includes("smorrebrod");
  // A trip edge day whose hours are capped by an arrival or departure time —
  // the last day you must leave by a set time, or a short arrival day. The
  // "make a day of it" / breakfast tip cards don't account for that cutoff, so
  // they're suppressed on these days; the time-aware suggestion pills still show.
  const limitedEdgeDay = (d) =>
    !!tripValid &&
    ((d.day === tripValid.planDays && tripValid.lastBy != null) ||
      (d.day === 1 && tripValid.firstFrom != null));
  // Tip ids relevant to one specific day of the built plan (rendered as a
  // compact card on that day, not stacked on top of the whole plan). Breakfast
  // tips only make sense when the place actually OPENS the day — a tip to
  // "start with breakfast" is useless when the spot is 4th on the list.
  const tipsForDay = (d) => {
    const tips = [];
    const first = d.items[0];
    if (!first || limitedEdgeDay(d)) return tips;
    if (
      first.id === "torvehallerne" &&
      !tipsDismissed.includes("market") &&
      (!marketHasSmorrebrod || !selected.includes("latinquarter") || !selected.includes("sanktpeders"))
    )
      tips.push("market");
    if (first.cluster === "harbour" && !tipsDismissed.includes("harbour"))
      tips.push("harbour");
    if (first.cluster === "christianshavn" && !tipsDismissed.includes("christianshavn"))
      tips.push("christianshavn");
    return tips;
  };
  // each tip shows once per plan — on the FIRST day that qualifies
  const planTipDay = {};
  (plan || []).forEach((d, di) => {
    tipsForDay(d).forEach((t) => {
      if (planTipDay[t] === undefined) planTipDay[t] = di;
    });
  });

  // Area-completion suggestions (see AREA_SIGNATURES): a day that's basically
  // one neighbourhood but missing its signature stop gets an add-card. Only
  // suggests spots not already anywhere in the plan, season-aware.
  const areaTipsForDay = (d) => {
    if (limitedEdgeDay(d)) return [];
    const stops = d.items.filter((s) => s.cluster);
    if (stops.length < 2 || stops.length > 4) return [];
    const counts = {};
    stops.forEach((s) => { counts[s.cluster] = (counts[s.cluster] || 0) + 1; });
    const [dom, n] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (stops.length - n > 1) return []; // not really a one-area day
    if (dom === "harbour" || dom === "daytrip" || dom === "partners") return [];
    // an early-closing add (a museum) is useless on a day that already runs
    // late — estimate when it would slot in and drop it if it can't finish
    const parseClock = (c) => { const [h, m] = c.split(":").map(Number); return h + m / 60; };
    const fitsToday = (id) => {
      const s = spotById(id);
      const k = insertionIndexFor(d.items, s);
      const startH = k >= d.items.length || !d.timeline?.at?.[k] ? (d.timeline?.endH ?? 10) : parseClock(d.timeline.at[k]);
      return fitsBeforeClose(s, startH, month);
    };
    // The centre "make a day of it" box is intentionally omitted: the same
    // nearby picks already appear in the "Room for more — ideas that fit"
    // list below, so a second suggestion block here is redundant.
    if (dom === "indre-by") return [];
    const sig = AREA_SIGNATURES[dom];
    if (!sig) return [];
    if (sig.kind === "bakery" && stops.some((s) => s.crawl)) return []; // day already has its bakery
    const add = sig.ids
      .filter((id) => {
        const s = spotById(id);
        return s && !selected.includes(id) && avail(s);
      })
      .filter(fitsToday)
      .slice(0, 1);
    if (!add.length) return [];
    return [{ key: `area-${dom}`, kicker: `${sig.label.toUpperCase()} DAY TIP`, text: sig.text, add }];
  };
  // each area tip shows once per plan — on the first day that qualifies
  const planAreaTipDay = {};
  (plan || []).forEach((d, di) => {
    areaTipsForDay(d).forEach((t) => {
      if (planAreaTipDay[t.key] === undefined) planAreaTipDay[t.key] = di;
    });
  });

  // Late-arrival rescue: a short Day 1 leaves Amalienborg & the Marble Church
  // off the curated harbour day. They're a ~15-min walk from the old-town Day 2
  // start, so offer to add them there — swapping out Rundetårn to keep that day
  // balanced. User's choice (one tap); nothing moves on its own.
  const royalsMissing =
    !!tripValid &&
    tripValid.firstFrom != null &&
    !!plan &&
    plan[0]?.items.some((s) => s.id === "nyhavn") &&
    plan[0]?.items.some((s) => s.id === "canaltour") &&
    plan.every((d) => !d.items.some((s) => s.id === "amalienborg")) &&
    plan.every((d) => !d.items.some((s) => s.id === "marmorkirken"));
  const royalDayIdx = royalsMissing ? (plan || []).findIndex((d) => d.items.some((s) => s.id === "rundetaarn")) : -1;
  const showRoyalRescue = royalDayIdx >= 0 && !tipsDismissed.includes("late-royals");
  // Accept: put Marmorkirken + Amalienborg at the start of the old-town day and
  // drop Rundetårn. The day is pinned so a later re-fit won't reshuffle it.
  const addRoyalsToOldTownDay = () => {
    if (royalDayIdx < 0) return;
    setSelected((sel) => [...sel.filter((id) => id !== "rundetaarn"), "amalienborg", "marmorkirken"]);
    setPlan((prev) => {
      if (!prev) return prev;
      return prev.map((d, i) => {
        if (i !== royalDayIdx) return d;
        const rest = d.items.filter((s) => !["rundetaarn", "amalienborg", "marmorkirken"].includes(s.id));
        const items = [spotById("marmorkirken"), spotById("amalienborg"), ...rest].filter(Boolean);
        return rebuildDayOrder(items, { ...d, pinnedCurated: true }, month);
      });
    });
    toast.success(`Added Amalienborg & the Marble Church to your Day ${royalDayIdx + 1} morning — Rundetårn swapped out to keep the day balanced.`);
  };


  const changeMonth = (m) => {
    const next = month === m ? "any" : m;
    setMonth(next);
    // drop any selected spots that aren't available in the new month
    setSelected((sel) => sel.filter((id) => isAvailable(spotById(id), next)));
  };

  // "Not sure" mode — pack the picked spots into the fewest doable days.
  // Pace sets the day ceiling: Relaxed is the standard ~8.5h day, Packed fills
  // to ~10h — same spots, fewer days. Curated-itinerary selections start at 3
  // days. Up to 12 days: visitors may add as much as they want (the manual day
  // selector stays 1–7). Very heavy selections fall back to balanced packing.
  const autoDaysFor = (ids, m, pz = pace) => {
    const cap = pz === "packed" ? 10 : 8.5; // day ceiling we aim under
    const looseCap = pz === "packed" ? 11 : 9; // last-resort ceiling at the full 12-day spread
    const over = (cand, lim) => cand.some((x) => x.hours > lim);
    const curated = ids.filter((id) => CURATED_IDS.has(id)).length >= 12;
    // the locked base is as long as the itinerary days touched — never ask
    // extendCuratedPlan for fewer days than that (they can never be dropped)
    const baseDays = curated ? ITINERARY_ALL.filter((d) => d.some((id) => ids.includes(id))).length : 0;
    const startD = curated ? Math.max(3, baseDays) : 1;
    if (curated) {
      // Curated selections keep the standard 8.5h bar at any pace — the
      // suggested itinerary is a curated product; pace applies when we
      // re-cluster freely below. (With a looser bar here, the locked days
      // "fit" at absurd day counts and Packed ends up LONGER than Relaxed.)
      for (let d = startD; d <= 12; d++) {
        const cand = genCurated(ids, d, m);
        if (!over(cand, 8.5)) return { plan: cand, days: d };
      }
      // The locked suggested days can't hold this selection without rushing —
      // a hand-picked range of old-town + harbour spots trips the itinerary
      // heuristic too, and its locked days then stay over the line at ANY day
      // count. Don't declare the trip impossible: re-cluster freely instead.
    }
    for (let d = startD; d <= 12; d++) {
      const cand = genPlan(ids, d, m);
      if (!over(cand, cap)) return { plan: cand, days: d };
    }
    const effH = ids.reduce((t, id) => {
      const s = spotById(id);
      return t + (s ? (s.id === "tivoli" ? 3 : s.dur) : 0);
    }, 0);
    const minD = Math.max(startD, Math.ceil(effH / cap));
    for (let d = minD; d <= 12; d++) {
      const cand = genBalanced(ids, d, m, cap);
      if (!over(cand, cap)) return { plan: cand, days: d };
    }
    // last resort: a long same-area day still beats splitting a
    // neighbourhood across days — one try at the full 12-day spread
    const loose = genBalanced(ids, 12, m, looseCap);
    if (!over(loose, looseCap)) return { plan: loose, days: 12 };
    return null;
  };

  // Switch the auto-pack pace — an on-screen auto plan re-packs on the spot
  const changePace = (p) => {
    if (p === pace) return;
    setPace(p);
    if (daysAuto && plan && selected.length) {
      const auto = autoDaysFor(selected, month, p);
      if (auto) {
        setDays(auto.days);
        setPlan(finalize(auto.plan));
      }
    }
    toast.success(
      p === "packed" ? "Packed pace — fuller days, fewer of them." : "Relaxed pace — easier days, more of them."
    );
  };

  const build = () => {
    if (!selected.length) {
      toast.error("Pick a few spots first!");
      return;
    }
    // built without dates or a "don't know" → reveal the month/day pickers so
    // the defaults used (any month, current day count) are visible and editable
    if (!whenChosen) setNoDates(true);
    // a selection anchored on the suggested itinerary is never re-shuffled:
    // curated days stay locked, extras join same-area days or new days
    const curatedHits = selected.filter((id) => CURATED_IDS.has(id)).length;
    if (daysAuto) {
      const auto = autoDaysFor(selected, month);
      if (!auto) {
        setPlan(null);
        setWarning("Even 12 days can't hold all that without rushing — remove a few spots and try again.");
        toast.error("That won't fit in 12 days");
        return;
      }
      setDays(auto.days);
      setWarning(null);
      setPlan(finalize(auto.plan));
      toast.success(`You need ${auto.days} day${auto.days > 1 ? "s" : ""} for this — done.`);
      setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      return;
    }
    // the itinerary grew to 5 days: a curated base can be longer than the day
    // count asked for — bump the count up so locked days are never dropped
    let effDays = days;
    if (curatedHits >= 12) {
      const base = ITINERARY_ALL.filter((d) => d.some((id) => selected.includes(id))).length;
      if (base > effDays) {
        effDays = base;
        setDays(base);
      }
    }
    const next =
      curatedHits >= 12 ? genCurated(selected, effDays, month) : genPlan(selected, effDays, month);
    // Hard stop past ~12 hours on a day — the trip is over-packed for the days chosen.
    const overDay = next.find((d) => d.hours > 12);
    if (overDay) {
      setPlan(null);
      setWarning(
        `You've chosen too many activities for ${effDays} day${effDays > 1 ? "s" : ""}. Keep each day to about 12 hours — add another day or remove a few spots.`
      );
      toast.error("Too many activities for the days you picked");
      return;
    }
    setWarning(null);
    setPlan(finalize(next));
    // drop down to the finished plan instead of leaving them on the button
    setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };

  // remove one stop from a day after the plan is built. Only that day reflows —
  // every other day keeps the visitor's exact arrangement, and an emptied day
  // simply becomes a free day instead of reshuffling the whole trip.
  const removeItem = (dayNum, spotId) => {
    setSelected((sel) => sel.filter((x) => x !== spotId));
    setPlan((prev) =>
      prev
        ? prev.map((d) =>
            d.day === dayNum
              ? rebuildDayOrder(d.items.filter((s) => s.id !== spotId), d, month)
              : d
          )
        : prev
    );
  };

  // Rearrange a single activity within its day. The day's stops keep the exact
  // order the user sets, and both the mini-map and the "Navigate this day" route
  // follow that order automatically.
  const moveItem = (dayIdx, from, to) => {
    setPlan((prev) => {
      if (!prev) return prev;
      const day = prev[dayIdx];
      if (!day || from == null || to == null || from === to || to < 0 || to >= day.items.length) return prev;
      const items = [...day.items];
      const [it] = items.splice(from, 1);
      items.splice(to, 0, it);
      return prev.map((d, i) => (i === dayIdx ? rebuildDayOrder(items, d, month) : d));
    });
  };

  // drag or arrow-move a whole day to reorder the trip (e.g. to fit arrival / hotel)
  const moveDay = (from, to) => {
    if (from == null || to == null || from === to || to < 0) return;
    setPlan((prev) => {
      if (!prev || to >= prev.length) return prev;
      const arr = [...prev];
      const [it] = arr.splice(from, 1);
      arr.splice(to, 0, it);
      // a moved day gets its new date, start time and closing-day flags
      // a moved day gets its new date, start time and closing-day flags
      return arr.map((d, i) => (d.day === i + 1 ? d : rebuildDayOrder(d.items, { ...d, day: i + 1 }, month)));
    });
  };

  // The long share link carries the exact day-by-day arrangement (";"-separated
  // Share links always point at the public production domain, never the preview host.
  const SITE_ORIGIN = "https://cphhighlights.com";

  // days) whenever a plan exists, so opening it never re-shuffles the trip.
  const tripLink = () => {
    const ids = plan
      ? plan.map((d) => d.items.map((s) => s.id).join(",")).join(";")
      : selected.join(",");
    return `${SITE_ORIGIN}/planner?trip=${encodeURIComponent(`${month}~${days}~${ids}`)}`;
  };

  // cache key for the short link — includes the arrangement, so reordering
  // stops or moving days invalidates the cached URL
  const tripSig = () =>
    `${month}~${days}~${
      plan ? plan.map((d) => d.items.map((s) => s.id).join(",")).join(";") : [...selected].sort().join(",")
    }~${JSON.stringify(tripDates)}~${hotel?.name || ""}`;

  // Create (and cache) a short, pretty share link like /plan/ab12cd via the backend.
  // Falls back to the long ?trip= link if the backend is unreachable.
  const getShareUrl = async () => {
    if (!selected.length) return tripLink();
    const sig = tripSig();
    if (shareCacheRef.current.sig === sig && shareCacheRef.current.url) {
      return shareCacheRef.current.url;
    }
    try {
      const { data } = await axios.post(`${API}/api/plans`, {
        month: String(month),
        days: plan ? plan.length : days,
        spots: selected,
        // the exact day-by-day arrangement, so the shared plan opens untouched
        ...(plan ? { day_spots: plan.map((d) => d.items.map((s) => s.id)) } : {}),
        // the visitor's own pasted-link stops ride along in the share link
        ...(customSpots.length ? { custom_spots: customSpots } : {}),
        ...(tripValid || hotel
          ? { trip: { start: tripDates.start, end: tripDates.end, arriveFrom: tripDates.arriveFrom, leaveBy: tripDates.leaveBy, hotel } }
          : {}),
      });
      const url = `${SITE_ORIGIN}/plan/${data.code}`;
      shareCacheRef.current = { sig, url };
      return url;
    } catch (e) {
      return tripLink();
    }
  };

  // A walking-route deep link for one day's stops — opens the user's Google/Apple Maps app
  const dayMapsUrl = (items) => {
    const pts = (items || []).filter((s) => s.coords).map((s) => `${s.coords[0]},${s.coords[1]}`);
    if (!pts.length) return null;
    if (pts.length === 1) return `https://www.google.com/maps/search/?api=1&query=${pts[0]}`;
    const origin = pts[0];
    const destination = pts[pts.length - 1];
    const waypoints = pts.slice(1, -1).join("|");
    const base = `https://www.google.com/maps/dir/?api=1&travelmode=walking&origin=${origin}&destination=${destination}`;
    return waypoints ? `${base}&waypoints=${encodeURIComponent(waypoints)}` : base;
  };

  const handleShowQR = async () => {
    if (!plan) return;
    if (showQR) {
      setShowQR(false);
      return;
    }
    const url = await getShareUrl();
    setQrUrl(url);
    setShowQR(true);
  };

  const shareIntro = () => `I planned this Copenhagen itinerary 🇩🇰`;

  const planText = (url) =>
    `${shareIntro()}\n\n` +
    plan
      .map(
        (d) =>
          `DAY ${d.day}${d.area ? ` — ${d.area}` : ""} (~${fmtHours(daytimeHours(d))}${endsWithTivoli(d) ? " + Tivoli in the evening" : ""})\n` +
          (d.items.length
            ? d.items.map((s) => `  • ${s.name}${s.note ? ` (${s.note})` : ""} (${spotDur(s)})`).join("\n")
            : "  • A free day — wander, rest or add more")
      )
      .join("\n\n") +
    `\n\nSee & tweak my plan: ${url}` +
    `\n\nBuilt with CPH Highlights · The Modern Guide`;

  const handleShare = async () => {
    if (!plan) return;
    const url = await getShareUrl();
    const text = planText(url);
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Copenhagen itinerary", text });
      } else {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
        toast.success("Itinerary + link copied to clipboard!");
      }
    } catch (e) {
      /* cancelled */
    }
  };

  // Render the plan as a 9:16 image so it can be posted straight to an Instagram story
  const handleStory = async () => {
    if (!plan) return;
    const W = 1080;
    const H = 1920;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    // preload the real brand fonts so the canvas doesn't fall back to system fonts
    try {
      await Promise.all([
        document.fonts.load("800 116px 'Plus Jakarta Sans'"),
        document.fonts.load("700 60px 'Plus Jakarta Sans'"),
        document.fonts.load("700 40px 'Nunito Sans'"),
        document.fonts.load("800 40px 'Nunito Sans'"),
      ]);
    } catch (e) {
      /* fall back to system fonts */
    }

    const DISP = "'Plus Jakarta Sans', sans-serif";
    const BODY = "'Nunito Sans', sans-serif";
    const DAY_COLORS = festive
      ? ["#DF3226", "#0E5C3A", "#B7791F", "#7A1420", "#125C40", "#C9A227"]
      : ["#DF3226", "#1E3A8A", "#0E9F6E", "#E8590C", "#7C3AED", "#0891B2"];

    const fit = (text, maxW, weight, size, family) => {
      let s = size;
      do {
        ctx.font = `${weight} ${s}px ${family}`;
        if (ctx.measureText(text).width <= maxW) break;
        s -= 2;
      } while (s > 12);
      return s;
    };

    const rr = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    };

    // ---- background: solid brand orange — deep pine green for Nov/Dec trips ----
    ctx.fillStyle = festive ? "#0E5C3A" : "#DF3226";
    ctx.fillRect(0, 0, W, H);
    if (festive) {
      // snowfall — seeded so the scatter is stable between renders;
      // drawn before the content card, which covers the middle
      let seed = 7;
      const rand = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
      for (let i = 0; i < 110; i++) {
        ctx.globalAlpha = 0.18 + rand() * 0.45;
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        ctx.arc(rand() * W, rand() * H, 2 + rand() * 5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      // the fun part — lights up top, Rudolph by the title, a snowman and a
      // present holding down the bottom corners
      drawLightsString(ctx, W);
      drawReindeer(ctx, W - 170, 446, 1.1);
      drawSnowman(ctx, 150, H - 165, 1);
      drawPresent(ctx, W - 165, H - 168, 1);
    }

    const M = 90;
    // ---- wordmark (pushed down into Instagram's top safe zone) ----
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#F9F8F6";
    ctx.font = `800 46px ${DISP}`;
    ctx.fillText("CPH Highlights", M, 228);
    ctx.fillStyle = "#FDE047";
    ctx.fillText(".", M + ctx.measureText("CPH Highlights").width, 228);
    ctx.font = `800 22px ${BODY}`;
    ctx.fillStyle = "rgba(249,248,246,0.55)";
    ctx.fillText("T H E   M O D E R N   G U I D E", M, 268);

    // ---- title ----
    // A plan made only of Christmas-walk stops is titled "My Copenhagen
    // Christmas walk"; the Extreme Day Trip gets "My EDT to Copenhagen";
    // anything else keeps "My Copenhagen itinerary".
    const xmasIds = new Set(XMAS_WALK);
    const xmasOnly =
      plan.length === 1 && plan[0].items.length > 0 && plan[0].items.every((it) => xmasIds.has(it.id));
    const isEdt = plan.some((d) => d.edt);
    const titleLine1 = isEdt ? "My EDT to" : "My Copenhagen";
    const titleLine2 = isEdt ? "Copenhagen" : xmasOnly ? "Christmas walk" : "itinerary";
    ctx.fillStyle = "#F9F8F6";
    const t1 = fit(titleLine1, W - M * 2, 800, 118, DISP);
    ctx.font = `800 ${t1}px ${DISP}`;
    ctx.fillText(titleLine1, M, 424);
    ctx.fillStyle = "#FDE047";
    const t2 = fit(titleLine2, W - M * 2, 800, 118, DISP);
    ctx.font = `800 ${t2}px ${DISP}`;
    const titleY2 = 424 + t1 + 4;
    ctx.fillText(titleLine2, M, titleY2);
    // accent underline
    ctx.fillStyle = "#F9F8F6";
    rr(M + 2, titleY2 + 24, 128, 12, 6);
    ctx.fill();

    // ---- quick stats row ----
    const totalStops = plan.reduce((t, d) => t + d.items.length, 0);
    const totalHours = plan.reduce((t, d) => t + (d.hours || 0), 0);
    const stats = `${days} days · ${totalStops} stops · ~${fmtHours(totalHours)}`;
    ctx.font = `700 30px ${BODY}`;
    ctx.fillStyle = "rgba(249,248,246,0.78)";
    ctx.fillText(stats, M, titleY2 + 86);

    // ---- content card (rounded, soft shadow) ----
    const cardX = M;
    const cardY = titleY2 + 128;
    const cardW = W - M * 2;
    const cardH = H - cardY - 340;
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = 60;
    ctx.shadowOffsetY = 28;
    ctx.fillStyle = "#F9F8F6";
    rr(cardX, cardY, cardW, cardH, 48);
    ctx.fill();
    ctx.restore();

    const pad = 64;
    const totalRows = plan.reduce((t, d) => t + Math.max(1, d.items.length), 0) + plan.length;
    const avail = cardH - pad * 2;
    const rowH = Math.max(30, Math.min(58, avail / (totalRows + plan.length * 1.1)));
    const dayFont = Math.round(rowH * 0.8);
    const itemFont = Math.round(rowH * 0.62);
    // would the tightest single column still spill past the card? then the
    // trip is long — flow whole days across two columns instead
    const tightestH = plan.reduce((t, d) => t + (Math.max(1, d.items.length) + 1.75) * 30, 0);
    const twoCol = tightestH > avail;

    if (twoCol) {
      // ---- two-column layout for long trips ----
      const colGap = 40;
      const cw = (cardW - pad * 2 - colGap) / 2;
      const dayRows = plan.map((d) => Math.max(1, d.items.length) + 1.75);
      const totR = dayRows.reduce((a, b) => a + b, 0);
      let acc = 0;
      let split = 0;
      for (let i = 0; i < plan.length; i++) {
        acc += dayRows[i];
        split = i + 1;
        if (acc >= totR / 2) break;
      }
      split = Math.max(1, Math.min(plan.length - 1, split));
      const maxColRows = Math.max(
        dayRows.slice(0, split).reduce((a, b) => a + b, 0),
        dayRows.slice(split).reduce((a, b) => a + b, 0)
      );
      const cRowH = Math.max(22, Math.min(46, avail / maxColRows));
      const cDayFont = Math.round(cRowH * 0.8);
      const cItemFont = Math.round(cRowH * 0.62);

      [plan.slice(0, split), plan.slice(split)].forEach((colDays, ci) => {
        const baseX = cardX + pad + ci * (cw + colGap);
        let cy = cardY + pad + cDayFont;
        colDays.forEach((d) => {
          const accent = DAY_COLORS[(d.day - 1) % DAY_COLORS.length];
          // numbered pill
          const pr = Math.round(cRowH * 0.5);
          const cxp = baseX + pr;
          ctx.fillStyle = accent;
          ctx.beginPath();
          ctx.arc(cxp, cy - cDayFont * 0.32, pr, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#fff";
          ctx.font = `800 ${Math.round(pr * 1.15)}px ${DISP}`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(d.day), cxp, cy - cDayFont * 0.3);
          ctx.textBaseline = "alphabetic";
          // area label + hours chip
          const hrs = `~${fmtHours(d.hours)}`;
          ctx.font = `800 ${Math.round(cItemFont * 0.8)}px ${BODY}`;
          const chipW = ctx.measureText(hrs).width + 30;
          const chipH = Math.round(cItemFont * 1.35);
          const label = d.area || `Day ${d.day}`;
          const labelSize = fit(label, cw - pr * 2 - 34 - chipW, 800, cDayFont, DISP);
          ctx.textAlign = "left";
          ctx.fillStyle = "#122A4E";
          ctx.font = `800 ${labelSize}px ${DISP}`;
          ctx.fillText(label, cxp + pr + 20, cy);
          ctx.fillStyle = "#FDE047";
          rr(baseX + cw - chipW, cy - chipH * 0.72, chipW, chipH, chipH / 2);
          ctx.fill();
          ctx.fillStyle = "#122A4E";
          ctx.textAlign = "center";
          ctx.fillText(hrs, baseX + cw - chipW / 2, cy - chipH * 0.72 + chipH * 0.68);
          ctx.textAlign = "left";
          cy += cRowH * 1.05;

          if (d.items.length) {
            d.items.forEach((s) => {
              ctx.fillStyle = accent;
              ctx.beginPath();
              ctx.arc(baseX + 6, cy - cItemFont * 0.32, 5, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = "#1A1A1A";
              const nameSize = fit(s.name, cw - 130, 700, cItemFont, BODY);
              ctx.font = `700 ${nameSize}px ${BODY}`;
              ctx.fillText(s.name, baseX + 28, cy);
              ctx.textAlign = "right";
              ctx.fillStyle = "#8A8A8A";
              ctx.font = `700 ${Math.round(cItemFont * 0.76)}px ${BODY}`;
              ctx.fillText(spotDur(s), baseX + cw, cy);
              ctx.textAlign = "left";
              cy += cRowH;
            });
          } else {
            ctx.fillStyle = "#8A8A8A";
            ctx.font = `700 ${cItemFont}px ${BODY}`;
            ctx.fillText("A free day — wander & rest", baseX + 28, cy);
            cy += cRowH;
          }
          cy += cRowH * 0.7;
        });
      });
    } else {
    // ---- single column (+ tiny map strip when the list leaves room) ----
    let y = cardY + pad + dayFont;

    plan.forEach((d, di) => {
      const accent = DAY_COLORS[di % DAY_COLORS.length];
      // numbered pill
      const pr = Math.round(rowH * 0.5);
      const cxp = cardX + pad + pr;
      ctx.fillStyle = accent;
      ctx.beginPath();
      ctx.arc(cxp, y - dayFont * 0.32, pr, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = `800 ${Math.round(pr * 1.15)}px ${DISP}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(d.day), cxp, y - dayFont * 0.3);
      ctx.textBaseline = "alphabetic";
      // day area label
      ctx.textAlign = "left";
      ctx.fillStyle = "#122A4E";
      const label = d.area || `Day ${d.day}`;
      const labelSize = fit(label, cardW - pad * 2 - pr * 2 - 150, 800, dayFont, DISP);
      ctx.font = `800 ${labelSize}px ${DISP}`;
      ctx.fillText(label, cxp + pr + 22, y);
      // hours chip
      const hrs = `~${fmtHours(d.hours)}`;
      ctx.font = `800 ${Math.round(itemFont * 0.8)}px ${BODY}`;
      const chipW = ctx.measureText(hrs).width + 34;
      const chipH = Math.round(itemFont * 1.35);
      ctx.fillStyle = "#FDE047";
      rr(cardX + cardW - pad - chipW, y - chipH * 0.72, chipW, chipH, chipH / 2);
      ctx.fill();
      ctx.fillStyle = "#122A4E";
      ctx.textAlign = "center";
      ctx.fillText(hrs, cardX + cardW - pad - chipW / 2, y - chipH * 0.72 + chipH * 0.68);
      ctx.textAlign = "left";
      y += rowH * 1.05;

      if (d.items.length) {
        d.items.forEach((s) => {
          ctx.fillStyle = accent;
          ctx.beginPath();
          ctx.arc(cardX + pad + 6, y - itemFont * 0.32, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#1A1A1A";
          const nameSize = fit(s.name, cardW - pad * 2 - 150, 700, itemFont, BODY);
          ctx.font = `700 ${nameSize}px ${BODY}`;
          ctx.fillText(s.name, cardX + pad + 28, y);
          ctx.textAlign = "right";
          ctx.fillStyle = "#8A8A8A";
          ctx.font = `700 ${Math.round(itemFont * 0.76)}px ${BODY}`;
          ctx.fillText(spotDur(s), cardX + cardW - pad, y);
          ctx.textAlign = "left";
          y += rowH;
        });
      } else {
        ctx.fillStyle = "#8A8A8A";
        ctx.font = `700 ${itemFont}px ${BODY}`;
        ctx.fillText("A free day — wander & rest", cardX + pad + 28, y);
        y += rowH;
      }
      y += rowH * 0.7;
    });

    // ---- tiny map strip at the bottom of the card — only when the day list
    // leaves room for it; a packed itinerary keeps the full text alone ----
    const cardBottom = cardY + cardH;
    const leftover = cardBottom - y - 28;
    if (leftover >= 220) {
      const mapH = Math.min(leftover, 460);
      try {
        await drawMiniMap(ctx, plan, cardX + pad / 2, cardBottom - mapH - 28, cardW - pad, mapH, festive);
      } catch (e) {
        /* tiles failed — the text list still stands on its own */
      }
    }
    }

    // ---- footer pill (lifted up out of Instagram's bottom safe zone) ----
    ctx.textAlign = "center";
    ctx.font = `800 30px ${BODY}`;
    const foot = "Plan yours at www.cphhighlights.com";
    const fw = ctx.measureText(foot).width + 60;
    ctx.fillStyle = "rgba(249,248,246,0.16)";
    rr(W / 2 - fw / 2, H - 300, fw, 68, 34);
    ctx.fill();
    ctx.fillStyle = "#F9F8F6";
    ctx.fillText(foot, W / 2, H - 255);
    ctx.textAlign = "left";

    const shortUrl = await getShareUrl();
    canvas.toBlob(async (blob) => {
      if (!blob) {
        toast.error("Couldn't create the story image.");
        return;
      }
      const file = new File([blob], "copenhagen-plan.png", { type: "image/png" });
      const shareData = {
        files: [file],
        title: "My Copenhagen plan",
        text: `${shareIntro()} ${shortUrl}`,
      };
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share(shareData);
          return;
        } catch (e) {
          if (e && e.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "copenhagen-plan.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Story image saved — post it to your Instagram story!");
    }, "image/png");
  };

  // Build one shareable image of the whole trip — every day pinned on one map.
  const handleTripMap = async () => {
    if (!plan) return;
    const id = toast.loading("Building your trip map…");
    try {
      const shortUrl = await getShareUrl();
      const blob = await buildTripMapImage(plan, "www.cphhighlights.com");
      if (!blob) {
        toast.error("Couldn't build the trip map.", { id });
        return;
      }
      const file = new File([blob], "copenhagen-trip-map.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "My Copenhagen trip map", text: `${shareIntro()} ${shortUrl}` });
          toast.dismiss(id);
          return;
        } catch (e) {
          if (e && e.name === "AbortError") {
            toast.dismiss(id);
            return;
          }
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "copenhagen-trip-map.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Trip map saved — every day, pinned in one shot!", { id });
    } catch (e) {
      toast.error("Couldn't build the trip map.", { id });
    }
  };

  const handlePrint = () => {
    if (!plan) return;
    const w = window.open("", "_blank", "width=820,height=1000");
    if (!w) {
      toast.error("Please allow pop-ups to print your plan.");
      return;
    }
    const daysHtml = plan
      .map(
        (d) => `
        <div class="day">
          <h2>Day ${d.day}${d.date ? ` <span class="area">· ${fmtDate(d.date)}</span>` : ""}${d.area ? ` <span class="area">· ${d.area}</span>` : ""} <span class="hrs">~${fmtHours(daytimeHours(d))}${endsWithTivoli(d) ? " + Tivoli in the evening" : ""}</span></h2>
          ${
            d.items.length
              ? d.items
                  .map(
                    (s, i) =>
                      `<div class="slot"><span class="dur">${d.timeline?.at?.[i] ? `${d.timeline.at[i]} · ` : ""}${spotDur(s)}</span><span><strong>${s.name}</strong>${s.note ? `<br/><span style="font-size:12px;color:#666;font-style:italic">${s.note}</span>` : ""}</span></div>` +
                      (d.timeline?.lunch?.afterIdx === i
                        ? `<div class="slot"><span class="dur">${d.timeline.lunch.at}</span><span><strong>${d.timeline.lunch.quick ? "Quick lunch · ~20 min" : "Lunch break · ~1h"}</strong>${d.timeline.lunch.venues?.length ? `<br/><span style="font-size:12px;color:#666">${d.timeline.lunch.venues.map((v) => v.name).join(" · ")}</span>` : ""}</span></div>`
                        : "")
                  )
                  .join("") +
                (d.timeline?.dinner?.picked
                  ? `<div class="slot"><span class="dur">${d.timeline.dinner.at}</span><span><strong>Dinner · ${d.timeline.dinner.picked.name}</strong><br/><span style="font-size:12px;color:#666">${d.timeline.dinner.picked.tag} · ~1½h</span></span></div>`
                  : "")
              : `<div class="slot"><em>A free day — wander, rest or add more.</em></div>`
          }
        </div>`
      )
      .join("");
    const bookRows = bookableStops(plan);
    const bookHtml = bookRows.length
      ? `<div class="day"><h2>Book ahead <span class="area">· tickets to sort out before you go</span></h2>${bookRows
          .map(
            ({ s, day }) =>
              `<div class="slot"><span class="dur">Day ${day.day}</span><span><strong>${s.name}</strong> <span style="font-size:12px;color:#DF3226;font-weight:800">${BOOK_AHEAD[s.id].kind}</span><br/><span style="font-size:12px;color:#666">${BOOK_AHEAD[s.id].tip} <a href="${s.gyg}" style="color:#DF3226">Tickets →</a></span></span></div>`
          )
          .join("")}</div>`
      : "";
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>My Copenhagen Plan</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@700;900&family=Manrope:wght@400;700;800&family=Playfair+Display:ital@1&display=swap');
        *{box-sizing:border-box;margin:0} body{font-family:Manrope,sans-serif;color:#0A0A0A;background:#F9F8F6;padding:48px}
        .brand{font-family:Outfit,sans-serif;font-weight:900;font-size:14px;letter-spacing:.1em}
        h1{font-family:Outfit,sans-serif;font-weight:900;font-size:44px;line-height:.9;margin:10px 0 6px}
        .accent{color:#DF3226}
        .day{border:2px solid #0A0A0A;box-shadow:6px 6px 0 #0A0A0A;padding:18px 22px;margin-bottom:22px;background:#fff;page-break-inside:avoid}
        .day h2{font-family:Outfit,sans-serif;font-weight:900;font-size:24px;border-bottom:2px solid #0A0A0A;padding-bottom:8px;margin-bottom:12px}
        .day h2 .area{font-weight:700;font-size:15px;color:#666}
        .day h2 .hrs{float:right;font-size:14px;color:#DF3226}
        .slot{display:flex;gap:14px;align-items:baseline;padding:6px 0}
        .dur{font-weight:800;font-size:12px;color:#1E3A8A;width:56px;flex:none}
        .slot strong{font-family:Outfit,sans-serif;font-size:16px}
        .foot{margin-top:24px;font-size:12px;color:#888}
      </style></head><body>
      <div class="brand">CPH Highlights<span class="accent">.</span> THE MODERN GUIDE</div>
      <h1>Your ${days}-day<br><span class="accent">Copenhagen</span> plan</h1>
      ${daysHtml}
      ${bookHtml}
      <div class="foot">Have a god tur — enjoy the city.</div>
      <div class="foot" style="margin-top:6px">© ${new Date().getFullYear()} CPH Highlights · The Modern Guide · www.cphhighlights.com</div>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <section id="planner" data-testid="planner" className="py-20 md:py-32 bg-[#F9F8F6] text-black">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">BUILD YOUR TRIP</p>
          </div>
          <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            Pick what you fancy<br />
            <span className="font-editorial italic font-600">We&apos;ll plan the days</span>
          </h1>
          <p className="font-body text-base md:text-lg text-black/60 max-w-2xl mt-6">
            Add your dates (or just a month) and the spots you want. We work out the
            days, group what&apos;s close together and keep every day realistic. We&apos;ll also
            make suggestions if you&apos;re in doubt. Save it, share it, print it.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-12 bg-white text-black border border-black shadow-brut-lg p-6 md:p-10">
            {/* when — exact dates first; the month buttons are the fallback */}
            <div className="mb-8">
              <p className="font-body font-800 text-xs tracking-[0.25em] mb-3">WHEN ARE YOU VISITING?</p>
              <TripDates
                value={tripDates}
                onChange={(v) => {
                  setTripDates(v);
                  if (tripDatesReady(v)) setNoDates(false);
                }}
                trip={trip}
              />
              {!tripValid && !noDates && (
                <button
                  type="button"
                  data-testid="planner-no-dates"
                  onClick={() => setNoDates(true)}
                  className="mt-3 inline-flex items-center gap-1.5 font-display font-800 text-sm bg-white border border-black shadow-brut-sm px-4 py-2 rounded-xl hover:-translate-y-0.5 transition-transform"
                >
                  <CalendarDays size={14} className="text-[#DF3226]" /> I don&apos;t know my dates yet
                </button>
              )}
              {!tripValid && noDates && (
                <div className="mt-5" data-testid="planner-month-fallback">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="font-body font-800 text-[10px] tracking-[0.2em] text-black/40">ROUGHLY WHICH MONTH?</p>
                    <button
                      type="button"
                      data-testid="planner-no-dates-back"
                      onClick={() => setNoDates(false)}
                      className="font-body font-700 text-xs text-black/50 hover:text-[#DF3226] transition-colors"
                    >
                      Actually, I know my dates
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {MONTHS.map((m) => (
                      <button
                        key={m.id}
                        data-testid={`planner-month-${m.id}`}
                        onClick={() => changeMonth(m.id)}
                        className={`font-display font-900 text-lg w-14 py-2.5 rounded-xl border transition-all ${
                          month === m.id
                            ? "bg-[#E8590C] text-white border-[#E8590C] shadow-none translate-x-[3px] translate-y-[3px]"
                            : "bg-white border-zinc-200 shadow-brut-sm hover:-translate-y-0.5"
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <p className="font-body text-xs text-black/40 mt-2">
                    {month === "any"
                      ? "Pick a month to filter seasonal spots and get weather tips — or leave it to see everything."
                      : `Showing what's on in ${MONTHS.find((x) => x.id === month)?.full}.`}
                  </p>
                </div>
              )}
            </div>

            {/* days — shown once the visitor has dates or has said they don't know them */}
            {whenChosen && (
            <div className="mb-8" data-testid="planner-days-section">
              <p className="font-body font-800 text-xs tracking-[0.25em] mb-3">HOW MANY DAYS?</p>
              {tripValid ? (
                <div data-testid="planner-days-from-dates" className="flex flex-wrap gap-2">
                  {Array.from({ length: Math.min(tripValid.planDays, MAX_PLAN_DAYS) }, (_, i) => {
                    const dt = new Date(tripValid.planStart);
                    dt.setDate(dt.getDate() + i);
                    const isFirst = i === 0 && tripValid.firstFrom != null;
                    const isLast = i === Math.min(tripValid.planDays, MAX_PLAN_DAYS) - 1 && tripValid.lastBy != null;
                    return (
                      <div
                        key={i}
                        data-testid={`planner-dateday-${i + 1}`}
                        className="bg-[#E8590C] text-white border border-[#E8590C] rounded-xl px-3 py-2 min-w-[4.5rem] text-center"
                      >
                        <p className="font-display font-900 text-sm leading-none">Day {i + 1}</p>
                        <p className="font-body font-700 text-[10px] tracking-wide mt-1 opacity-90">{fmtDate(dt)}</p>
                        {(isFirst || isLast) && (
                          <p className="font-body font-700 text-[10px] mt-0.5 opacity-80">
                            {isFirst && `from ${fmtTime(Math.max(10, tripValid.firstFrom))}`}
                            {isFirst && isLast && " · "}
                            {isLast && `by ${fmtTime(tripValid.lastBy)}`}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
              <div className="flex gap-3">
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <button
                    key={d}
                    data-testid={`planner-days-${d}`}
                    onClick={() => {
                      setWarning(null);
                      setDaysAuto(false);
                      if (plan && d !== days) {
                        if (d > days) {
                          // growing the trip keeps every arranged day exactly as it
                          // is — fresh empty day(s) go on the end, and new picks
                          // join them without reshuffling what's already planned
                          setPlan((prev) => [
                            ...prev,
                            ...Array.from({ length: d - days }, (_, k) =>
                              buildDayFromItems([], prev.length + 1 + k, month)
                            ),
                          ]);
                          setDays(d);
                          return;
                        }
                        // shrinking the trip — re-spread the stops across fewer days
                        const next = genPlan(selected, d, month);
                        if (next.some((x) => x.hours > 12)) {
                          setWarning(
                            `${d} day${d > 1 ? "s" : ""} is too few for everything you picked — a day would run past 12 hours. Remove a few spots first.`
                          );
                          return;
                        }
                        setDays(d);
                        setPlan(finalize(next));
                        return;
                      }
                      setDays(d);
                    }}
                    className={`flex-1 md:flex-none md:w-14 font-display font-900 text-lg py-2.5 rounded-xl border transition-all ${
                      !daysAuto && days === d
                        ? "bg-[#E8590C] text-white border-[#E8590C] shadow-none translate-x-[3px] translate-y-[3px]"
                        : "bg-white border-zinc-200 shadow-brut-sm hover:-translate-y-0.5"
                    }`}
                  >
                    {d}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="planner-days-auto"
                  onClick={() => {
                    setWarning(null);
                    setDaysAuto(true);
                    if (plan) setPlan(null); // rebuilt on Build with the right day count
                  }}
                  className={`flex-1 md:flex-none md:w-auto md:px-4 font-display font-900 text-sm py-2.5 rounded-xl border transition-all ${
                    daysAuto
                      ? "bg-[#E8590C] text-white border-[#E8590C] shadow-none translate-x-[3px] translate-y-[3px]"
                      : "bg-white border-zinc-200 shadow-brut-sm hover:-translate-y-0.5"
                  }`}
                >
                  Not sure
                </button>
              </div>
              )}
              {daysAuto && !tripValid && (
                <p className="font-body text-xs text-black/50 mt-2">
                  Just pick your spots below — we&apos;ll work out how many days you need.
                </p>
              )}
              {daysAuto && !tripValid && (
                <div data-testid="planner-pace" className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="font-body font-800 text-[10px] tracking-[0.2em] text-black/40">PACE</span>
                  {[
                    { id: "relaxed", label: "Relaxed" },
                    { id: "packed", label: "Packed" },
                  ].map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`planner-pace-${p.id}`}
                      onClick={() => changePace(p.id)}
                      className={`font-display font-800 text-xs px-4 py-1.5 rounded-full border transition-all ${
                        pace === p.id
                          ? "bg-[#E8590C] text-white border-[#E8590C]"
                          : "bg-white border-zinc-200 shadow-brut-sm hover:-translate-y-0.5"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                  <span className="font-body text-[11px] text-black/45">
                    {pace === "relaxed" ? "easier days, more of them" : "fuller days, fewer of them"}
                  </span>
                </div>
              )}
            </div>
            )}

            {/* hotel / start point */}
            <div className={`mb-8 ${whenChosen ? "-mt-3" : ""}`}>
              <HotelInput hotel={hotel} onChange={setHotel} />
            </div>

            {/* spots */}
            <div className="mb-8">
              <div className="flex flex-col gap-1.5 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-body font-800 text-xs tracking-[0.25em]">WHAT DO YOU WANT TO SEE & DO?</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {selected.length > 0 && (
                    <button
                      type="button"
                      data-testid="planner-picker-reset"
                      onClick={() => { setSelected([]); setPlan(null); setWarning(null); }}
                      title="Clear the whole selection"
                      className="inline-flex items-center gap-1 font-body font-700 text-xs text-black/50 hover:text-[#DF3226] transition-colors"
                    >
                      <RotateCcw size={12} /> Reset
                    </button>
                  )}
                  {dismissed.length > 0 && (
                    <button
                      type="button"
                      data-testid="planner-restore-hidden"
                      onClick={restoreHidden}
                      title="Bring back the spots you hid"
                      className="inline-flex items-center gap-1 font-body font-700 text-xs text-black/50 hover:text-[#DF3226] transition-colors"
                    >
                      <RotateCcw size={12} /> Show {dismissed.length} hidden
                    </button>
                  )}
                  <span className="font-body font-700 text-xs text-[#DF3226]" data-testid="planner-selected-count">
                    {selected.length} selected
                  </span>
                </div>
              </div>
              {/* one-tap fills — the suggested itinerary, the Christmas walk, the bakery crawl */}
              <div className="mb-4 -mt-1">
              {days <= 5 && (
                <button
                  type="button"
                  data-testid="planner-autofill-itinerary"
                  onClick={() => {
                    // Never send people to a closed door: out-of-season curated
                    // stops are swapped for year-round neighbours (Reffen →
                    // Lille Bakery + Copenhagen Contemporary) or dropped (Our
                    // Saviour's spire climb in January) before the plan is built.
                    const SUBS = { reffen: ["lille", "cc"] };
                    const notes = [];
                    // Winter day 2 (Tivoli closed Jan–Feb): a proper smørrebrød
                    // lunch after Rundetårn and Sankt Peders Bageri after
                    // Gråbrødretorv fill the gap. The itinerary page keeps the
                    // summer line-up — this only shapes the plan built here.
                    const tivoliClosed = !avail(spotById("tivoli"));
                    const groups = ITINERARY_ALL.slice(0, days).map((g, gi) => {
                      const out = g.reduce((acc, id) => {
                        const s = spotById(id);
                        if (s && !avail(s)) {
                          const subs = SUBS[id];
                          if (subs) {
                            acc.push(...subs);
                            notes.push(`${s.name} is closed — ${subs.map((x) => spotById(x).name).join(" and ")} take its place.`);
                          } else if (!(id === "tivoli" && gi === 1)) {
                            notes.push(`${s.name} is closed that month — left out.`);
                          }
                          return acc;
                        }
                        acc.push(id);
                        return acc;
                      }, []);
                      if (gi === 1 && tivoliClosed) {
                        const insertAfter = (arr, afterId, addId) => {
                          const i = arr.indexOf(afterId);
                          if (i >= 0 && !arr.includes(addId)) arr.splice(i + 1, 0, addId);
                        };
                        insertAfter(out, "rundetaarn", "smorrebrod");
                        insertAfter(out, "graabroedretorv", "sanktpeders");
                        notes.push("Tivoli is closed in winter — day 2 gets a smørrebrød lunch and Sankt Peders Bageri instead.");
                      }
                      return out;
                    });
                    setSelected(groups.flat());
                    setWarning(null);
                    setPlan(finalize(planFromDays(groups, month)));
                    toast.success(
                      days >= 3
                        ? `Loaded our suggested ${days}-day itinerary — tweak anything you like.`
                        : `Loaded day ${days === 1 ? "1" : "1 & 2"} of our suggested itinerary — tweak anything you like.`
                    );
                    notes.forEach((n) => toast.info(n));
                    setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
                  }}
                  className="mt-3 w-full inline-flex items-start justify-start text-left gap-1.5 font-body font-700 text-xs text-black/45 hover:text-black transition-colors"
                >
                  <Mark size={13} className="shrink-0 mt-0.5" />
                  {days >= 5
                    ? "Fill in our suggested 5-day itinerary"
                    : days === 4
                      ? "Fill in the first 4 days of our itinerary"
                      : days === 3
                        ? "Fill in our suggested 3-day itinerary"
                        : days === 2
                          ? "Fill in the first 2 days of our itinerary"
                          : "Fill in Day 1 of our suggested itinerary"}
                </button>
              )}
              {(month === 11 || month === 12) && (
                <button
                  type="button"
                  data-testid="planner-autofill-christmas"
                  onClick={() => {
                    setDaysAuto(false);
                    setDays(1);
                    setSelected(XMAS_WALK);
                    setWarning(null);
                    setPlan(finalize(planFromDays([XMAS_WALK], month)));
                    toast.success("Loaded the Christmas walk — markets, gløgg and Tivoli's lights. Tweak anything you like.");
                    setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
                  }}
                  className="mt-2 w-full inline-flex items-start justify-start text-left gap-1.5 font-body font-700 text-xs text-black/45 hover:text-black transition-colors"
                >
                  <Mark size={13} className="shrink-0 mt-0.5" />
                  Fill the Christmas walk — markets, gløgg & Tivoli
                </button>
              )}
              {(month === 2 || month === 3) && (
                <button
                  type="button"
                  data-testid="planner-autofill-bakery"
                  onClick={() => {
                    setDaysAuto(false);
                    setDays(1);
                    setSelected(BAKERY_WALK);
                    setWarning(null);
                    setPlan(finalize(planFromDays([BAKERY_WALK], month)));
                    toast.success("Loaded the bakery crawl — eight stops of Fastelavn buns and cardamom. Tweak anything you like.");
                    setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
                  }}
                  className="mt-2 w-full inline-flex items-start justify-start text-left gap-1.5 font-body font-700 text-xs text-black/45 hover:text-black transition-colors"
                >
                  <Mark size={13} className="shrink-0 mt-0.5" />
                  Fill the bakery crawl — eight bakeries, Fastelavn season
                </button>
              )}
              </div>
              <PickerToggles value={pickerShow} onChange={setPickerShow} />
              <div className="space-y-8">
                {spotGroups.map(({ cid, items }) => {
                  return (
                    <div key={cid}>
                      <p className="font-body font-800 text-[11px] tracking-widest text-black/40 mb-3 flex items-center gap-1.5">
                        <MapPin size={12} /> {CLUSTERS[cid].toUpperCase()}
                      </p>
                      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 md:gap-3">
                        {items.map((s, gi) => {
                          const on = selected.includes(s.id);
                          const hint = seasonHint(s);
                          const closed = !avail(s);
                          const cat = spotCat(s.id);
                          // header only when the visible label changes (food & sweets share one) —
                          // and small areas skip category headers altogether (a flat list reads better)
                          const distinctLabels = new Set(items.map((i) => CAT_LABELS[spotCat(i.id)]));
                          const showCatHeaders = items.length > 5 && distinctLabels.size > 1;
                          const newCat = showCatHeaders && (gi === 0 || CAT_LABELS[cat] !== CAT_LABELS[spotCat(items[gi - 1].id)]);
                          return (
                            <Fragment key={s.id}>
                              {newCat && (
                                <p
                                  data-testid={`planner-cat-${cid}-${cat}`}
                                  className="col-span-full font-body font-800 text-[10px] tracking-widest text-black/35 mt-1 first:mt-0"
                                >
                                  {CAT_LABELS[cat]}
                                </p>
                              )}
                            <div
                              className={`group relative aspect-square rounded-xl overflow-hidden transition-all duration-300 ${
                                closed
                                  ? "grayscale opacity-50"
                                  : on
                                  ? "ring-[3px] ring-[#DF3226] shadow-brut -translate-y-0.5"
                                  : "ring-1 ring-black/5 shadow-sm hover:-translate-y-1 hover:shadow-brut"
                              }`}
                            >
                              <button
                                data-testid={`planner-spot-${s.id}`}
                                onClick={() => { if (!closed) toggle(s.id); }}
                                disabled={closed}
                                aria-pressed={on}
                                title={closed ? (seasonLabel(s) ? `${s.name} — ${seasonLabel(s)}` : hint ? `Open ${hint}` : s.name) : s.name}
                                className={`absolute inset-0 w-full h-full text-left ${closed ? "cursor-not-allowed" : ""}`}
                              >
                                <img
                                  src={spotImg(s.id)}
                                  alt={s.name}
                                  loading="lazy"
                                  decoding="async"
                                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                                {on && <div className="absolute inset-0 bg-[#DF3226]/25" />}

                                {/* top-left: season hint · top-right: selected check */}
                                <div className="absolute top-2.5 left-2.5 right-2.5 flex items-start justify-between gap-2">
                                  {closed && planDates.length ? (
                                    <span data-testid={`planner-spot-closed-${s.id}`} className="bg-[#DF3226] text-white font-body font-800 text-[10px] uppercase tracking-wide px-2 py-1 rounded-full">
                                      Closed on your dates
                                    </span>
                                  ) : hint ? (
                                    <span className="bg-white/90 backdrop-blur-sm text-black/70 font-body font-800 text-[10px] uppercase tracking-wide px-2 py-1 rounded-full">
                                      {hint}
                                    </span>
                                  ) : <span />}
                                  {on && (
                                    <span className="grid place-items-center w-6 h-6 rounded-full shrink-0 bg-[#DF3226] text-white">
                                      <Check size={14} strokeWidth={3} />
                                    </span>
                                  )}
                                </div>

                                {/* bottom: name + duration */}
                                <div className="absolute inset-x-0 bottom-0 p-2 md:p-2.5">
                                  <p className="font-display font-900 text-white leading-tight text-xs md:text-sm drop-shadow-sm">
                                    {s.name}
                                  </p>
                                  <p className="font-body font-800 text-[#DF3226] text-[10px] md:text-xs mt-0.5 flex items-center gap-1">
                                    <Clock size={10} /> {spotDur(s)}
                                  </p>
                                </div>
                              </button>

                              {/* help text — "what even is this": the spot's note.
                                  Desktop: overlay on hover. Mobile: a readable toast
                                  on ⓘ tap (the tiny card is too small for the text). */}
                              {s.note && !closed && (
                                <>
                                  <div
                                    data-testid={`planner-spot-note-${s.id}`}
                                    className="absolute inset-0 bg-black/90 p-3 hidden md:flex items-center pointer-events-none opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                                  >
                                    <p className="font-body font-700 text-white text-xs leading-snug">
                                      <span className="font-900">{s.name}</span> — {s.note}
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    data-testid={`planner-spot-info-${s.id}`}
                                    aria-label={`What is ${s.name}?`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toast(s.name, { description: s.note, duration: 6000 });
                                    }}
                                    className="absolute bottom-2 right-2 z-20 grid place-items-center w-6 h-6 rounded-full bg-white/85 text-black/70 shadow-sm active:scale-95 transition-transform md:hidden"
                                  >
                                    <Info size={13} strokeWidth={2.5} />
                                  </button>
                                </>
                              )}

                              {/* tickets — affiliate link for bookable spots: tiny, quiet, top-left so it never covers the card text */}
                              {s.gyg && !closed && (
                                <a
                                  href={s.gyg}
                                  target="_blank"
                                  rel="noopener noreferrer sponsored"
                                  data-testid={`planner-gyg-chip-${s.id}`}
                                  title={`Tickets & tours for ${s.name}`}
                                  className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm border border-black/10 text-black/70 font-body font-800 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm hover:bg-black hover:text-white hover:border-black transition-colors"
                                >
                                  <Ticket size={9} /> Tickets
                                </a>
                              )}

                              {/* dismiss — hide a spot you're not interested in for a cleaner overview */}
                              {!closed && !on && (
                                <button
                                  type="button"
                                  data-testid={`planner-dismiss-${s.id}`}
                                  onClick={() => dismissSpot(s.id)}
                                  title={`Not interested — hide ${s.name}`}
                                  aria-label={`Hide ${s.name}`}
                                  className="absolute top-2.5 right-2.5 z-20 grid place-items-center w-5 h-5 rounded-full bg-black/45 text-white backdrop-blur-sm shadow-sm hover:bg-[#DF3226] active:bg-[#DF3226] transition-colors"
                                >
                                  <X size={11} strokeWidth={3} />
                                </button>
                              )}
                            </div>
                            </Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* add your own stop — paste a Google Maps link */}
            <div className="mb-8 border-2 border-dashed border-zinc-300 rounded-xl px-4 py-4 bg-white/60">
              <p className="font-body font-800 text-xs tracking-[0.25em] mb-1.5">MISSING SOMETHING?</p>
              <p className="font-body text-xs text-black/60 mb-3 leading-relaxed">
                Paste a Google Maps link to any place — it becomes a real stop with a pin and walking times.
              </p>
              <div className="flex gap-2">
                <input
                  type="url"
                  data-testid="planner-custom-link-input"
                  value={customLink}
                  onChange={(e) => setCustomLink(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCustomStop()}
                  placeholder="https://maps.app.goo.gl/…"
                  className="flex-1 min-w-0 font-body text-sm bg-white border border-zinc-300 rounded-lg px-3 py-2.5 outline-none focus:border-black transition-colors"
                />
                <button
                  type="button"
                  data-testid="planner-custom-add-btn"
                  onClick={addCustomStop}
                  disabled={customBusy || !customLink.trim()}
                  className="font-display font-800 text-sm bg-black text-white px-4 py-2.5 rounded-lg hover:bg-[#DF3226] transition-colors disabled:opacity-40"
                >
                  {customBusy ? "…" : "Add"}
                </button>
              </div>
            </div>

            {warning && (
              <div
                data-testid="planner-warning"
                className="mb-5 flex items-start gap-3 bg-[#DF3226]/10 border border-[#DF3226]/30 text-[#9f2318] rounded-xl px-4 py-3.5"
              >
                <AlertTriangle size={18} className="shrink-0 mt-0.5 text-[#DF3226]" />
                <p className="font-body font-700 text-sm leading-snug">{warning}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2.5">
              <button
                data-testid="planner-generate"
                onClick={build}
                className="font-display font-900 text-base bg-black text-white px-6 py-2.5 rounded-full border border-zinc-200 hover:bg-[#DF3226] transition-colors inline-flex items-center gap-2"
              >
                Build my trip
              </button>
              {plan && (
                <button
                  data-testid="planner-reset"
                  onClick={() => { setPlan(null); setSelected([]); setWarning(null); }}
                  className="font-display font-800 text-base bg-white px-5 py-2.5 border border-zinc-200 inline-flex items-center gap-2 hover:bg-[#F9F8F6]"
                >
                  <RotateCcw size={16} /> Reset
                </button>
              )}
            </div>
          </div>
        </Reveal>

        {/* output */}
        <AnimatePresence>
          {plan && (
            <motion.div
              key="plan"
              ref={outputRef}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 scroll-mt-24"
            >
              {plan.length < 7 &&
                !plan.some((d) => d.edt) &&
                plan.every(
                  (d) =>
                    // a one-day, all-inner-city plan is eminently walkable —
                    // only suggest splitting once it runs past ~9 hours;
                    // a Tivoli evening doesn't count toward the daytime load
                    daytimeHours(d) >
                    (days === 1 && d.items.every((s) => s.cluster === "indre-by") ? 9 : 8)
                ) && (
                <div
                  data-testid="planner-longer-trip-hint"
                  className="mb-4 flex flex-wrap items-center gap-3 bg-white border border-black shadow-brut-sm rounded-xl px-4 py-3"
                >
                  <p className="font-body font-700 text-sm text-black/75 flex items-center gap-1.5">
                    <Lightbulb size={14} className="text-[#B8860B] shrink-0" />
                    {days === 1
                      ? `Your day is pretty packed — consider making this a ${days + 1}-day trip.`
                      : `All ${days} days are pretty packed — consider making this a ${days + 1}-day trip.`}
                  </p>
                  <button
                    type="button"
                    data-testid="planner-longer-trip-btn"
                    onClick={() => {
                      const nd = days + 1;
                      setDays(nd);
                      // every day is packed — that's why this hint shows — so
                      // re-spread the stops across the extra day instead of
                      // appending a blank one that helps no one
                      setPlan(finalize(genPlan(selected, nd, month)));
                      toast.success(`Re-spread your stops over ${nd} days.`);
                    }}
                    className="font-display font-800 text-xs bg-black text-white px-3.5 py-1.5 rounded-full hover:bg-[#DF3226] transition-colors"
                  >
                    Switch to {days + 1} days
                  </button>
                </div>
              )}
              {daysAuto && (
                <div
                  data-testid="planner-auto-days-banner"
                  className="mb-4 flex flex-wrap items-center gap-3 bg-[#DF3226] text-white border border-black shadow-brut-sm rounded-xl px-4 py-3"
                >
                  <p className="font-body font-700 text-sm text-white flex items-center gap-1.5">
                    <Mark size={14} className="text-white shrink-0" />
                    You need {days} day{days > 1 ? "s" : ""} for this — done.
                  </p>
                </div>
              )}
              {tripValid?.arrivalEvening && (
                <div className="mb-5">
                  <ArrivalEveningCard date={tripValid.arrivalEvening} from={tripDates.arriveFrom} hotel={hotel} month={month} />
                </div>
              )}
              <div data-testid="planner-output" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {plan.map((d, di) => (
                <motion.div
                  key={d.uid}
                  layout
                  data-testid={`planner-day-${d.day}`}
                  draggable
                  onDragStart={() => setDragIdx(di)}
                  onDragEnd={() => setDragIdx(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    moveDay(dragIdx, di);
                    setDragIdx(null);
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: dragIdx === di ? 0.4 : 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className={`bg-white text-black border shadow-brut p-6 flex flex-col cursor-grab active:cursor-grabbing ${
                    d.locked ? "border-[#DF3226] ring-2 ring-[#DF3226]/20" : "border-black"
                  }`}
                >
                  <div className="flex items-center justify-between border-b border-zinc-200 pb-3 mb-4">
                    <span className="font-display font-900 text-2xl inline-flex items-center gap-2">
                      <GripVertical size={18} className="text-black/25" />
                      <span>
                        Day {d.day}
                        {d.date && (
                          <span data-testid={`planner-day-date-${d.day}`} className="block font-body font-700 text-[11px] tracking-wide text-black/50 -mt-0.5">
                            {fmtDate(d.date)}
                            {di === 0 && tripValid?.firstFrom != null && ` · from ${fmtTime(Math.max(10, tripValid.firstFrom))}`}
                            {di === plan.length - 1 && tripValid?.lastBy != null && ` · leave by ${fmtTime(tripValid.lastBy)}`}
                          </span>
                        )}
                      </span>
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        data-testid={`planner-day-lock-${d.day}`}
                        onClick={() => toggleLock(di)}
                        aria-pressed={!!d.locked}
                        aria-label={d.locked ? "Unlock this day" : "Lock this day"}
                        title={d.locked ? "Locked — re-builds leave this day untouched" : "Lock this day so re-builds leave it as it is"}
                        className={`w-7 h-7 grid place-items-center border transition-colors ${
                          d.locked ? "bg-[#DF3226] border-[#DF3226] text-white" : "border-zinc-200 bg-white hover:bg-[#F1EDE3]"
                        }`}
                      >
                        {d.locked ? <Lock size={13} /> : <Unlock size={13} />}
                      </button>
                      <button
                        data-testid={`planner-day-earlier-${d.day}`}
                        onClick={() => moveDay(di, di - 1)}
                        disabled={di === 0}
                        aria-label="Move day earlier"
                        className="w-7 h-7 grid place-items-center border border-zinc-200 bg-white disabled:opacity-25 hover:bg-[#F1EDE3] transition-colors"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        data-testid={`planner-day-later-${d.day}`}
                        onClick={() => moveDay(di, di + 1)}
                        disabled={di === plan.length - 1}
                        aria-label="Move day later"
                        className="w-7 h-7 grid place-items-center border border-zinc-200 bg-white disabled:opacity-25 hover:bg-[#F1EDE3] transition-colors"
                      >
                        <ChevronRight size={14} />
                      </button>
                      <span className="inline-flex items-center gap-1 font-body font-800 text-xs text-[#DF3226] ml-1">
                        <Clock size={13} /> {daytimeHours(d) > 0 ? `~${fmtHours(daytimeHours(d))}` : "Evening only"}
                        {endsWithTivoli(d) && daytimeHours(d) > 0 && (
                          <span className="font-700 text-black/45">+ Tivoli evening</span>
                        )}
                      </span>
                    </div>
                  </div>
                  {d.area && (
                    <p className="font-body font-800 text-[11px] tracking-widest text-black/55 mb-4 flex items-center gap-1.5">
                      <MapPin size={12} /> {d.area.toUpperCase()}
                    </p>
                  )}
                  {d.walk && d.walk.min > 0 && (
                    <p
                      data-testid={`planner-day-walk-${d.day}`}
                      className="font-body font-700 text-[11px] text-black/55 -mt-2 mb-4 flex items-center gap-1.5"
                    >
                      <Footprints size={12} /> ~{fmtWalkMin(d.walk.min)} on foot · {d.walk.km.toFixed(1)} km covered
                    </p>
                  )}
                  {d.timeline && (
                    <p
                      data-testid={`planner-day-timeline-${d.day}`}
                      className="font-body font-700 text-[11px] text-black/55 -mt-2 mb-4 flex items-center gap-1.5"
                    >
                      <Clock size={12} /> {d.timeline.start} → {d.timeline.end}
                      {d.timeline.backAt && <span className="text-black/40">· back at the hotel ~{d.timeline.backAt}</span>}
                    </p>
                  )}
                  {(d.dkHoliday || d.seHoliday) && (
                    <div
                      data-testid={`planner-day-holiday-${d.day}`}
                      className="font-body -mt-2 mb-4 flex items-start gap-2 bg-[#FCEFC5] border border-[#E8C45A] rounded-lg px-3 py-2.5"
                    >
                      <CalendarDays size={15} className="shrink-0 mt-0.5 text-[#9A6B00]" />
                      <p className="font-700 text-[12px] md:text-[13px] leading-snug text-[#5c4300]">
                        {d.dkHoliday && (
                          <>
                            <span className="font-900">{d.dkHoliday}</span> — official Danish bank holiday. Many shops close and attractions run reduced or holiday hours; check ahead.
                          </>
                        )}
                        {d.seHoliday && (
                          <span className={d.dkHoliday ? "block mt-1.5" : ""}>
                            <span className="font-900">{d.seHoliday}</span> — official Swedish bank holiday (for the Malmö day trip). Expect many places in Sweden closed or on holiday hours.
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  {d.edt && (
                    <div
                      data-testid="planner-edt-ready"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 -mt-2 mb-4 flex-wrap"
                    >
                      <label htmlFor={`edt-ready-${d.day}`} className="font-body font-800 text-[11px] tracking-widest text-black/55 inline-flex items-center gap-1.5">
                        <Clock size={12} className="text-[#DF3226]" /> READY IN THE CITY AT
                      </label>
                      <input
                        id={`edt-ready-${d.day}`}
                        type="time"
                        step="300"
                        data-testid="planner-edt-ready-input"
                        value={edtReadyStr(d)}
                        onChange={(e) => setEdtReady(di, e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="font-body font-800 text-xs border border-zinc-300 rounded px-2 py-1 text-black bg-white focus:outline-none focus:ring-2 focus:ring-[#DF3226]/40"
                      />
                      {(d.edtOffset || 0) !== 0 && (
                        <span className="font-body font-700 text-[11px] text-black/45">ends ~{d.timeline?.end?.replace("~", "")}</span>
                      )}
                    </div>
                  )}
                  {d.timeline?.over && (
                    <p
                      data-testid={`planner-day-over-${d.day}`}
                      className="font-body font-700 text-[11px] text-[#DF3226] -mt-2 mb-4 flex items-center gap-1.5"
                    >
                      <AlertTriangle size={12} /> Runs to ~{d.timeline.over.end} but you need to leave by {d.timeline.over.by} — drop a stop or move one to another day.
                    </p>
                  )}
                  {(() => {
                    const closed = (d.closedIds || [])
                      .map((id) => d.items.find((x) => x.id === id))
                      .filter(Boolean);
                    // out-of-season stops (e.g. Christmas markets before mid-Nov)
                    // collapse into one line; weekly closing-day notes stay per-spot
                    const oos = closed.filter((s) => !isOpenOnDate(s, d.date));
                    const weekly = closed.filter((s) => isOpenOnDate(s, d.date));
                    const isFestive = (s) =>
                      s.id.startsWith("xmas-") || XMAS_WALK.includes(s.id) || /Nov/.test(seasonLabel(s) || "");
                    const cls = "font-body font-700 text-[11px] text-[#DF3226] -mt-2 mb-4 flex items-start gap-1.5";
                    const summaryCls = "font-body font-800 text-[13px] md:text-sm leading-snug text-[#DF3226] -mt-1 mb-4 flex items-start gap-2 bg-[#DF3226]/8 rounded-lg px-3 py-2.5";
                    const line = (s) => (
                      <p key={s.id} data-testid={`planner-day-closed-${d.day}-${s.id}`} className={cls}>
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {s.name} is {closureReason(s, d.date)} — {fmtDate(d.date)} won&apos;t work. Move it to another day.
                      </p>
                    );
                    const out = [];
                    if (oos.length >= 2) {
                      // the date the LAST of them opens → "all open from X"
                      const opens = oos.map((s) => nextOpeningDate(s, d.date)).filter(Boolean);
                      const allOpen = opens.length === oos.length ? new Date(Math.max(...opens.map((x) => +x))) : null;
                      out.push(
                        <p key="oos-summary" data-testid={`planner-day-closed-summary-${d.day}`} className={summaryCls}>
                          <AlertTriangle size={16} className="shrink-0 mt-0.5" />{" "}
                          {oos.every(isFestive)
                            ? `${oos.length} Christmas-season stops aren't open yet on ${fmtDate(d.date)} — ${allOpen ? `all open from ${fmtDate(allOpen)}` : "the markets, Tivoli & festive spots open from mid-November"}. Move them to a later day (or drop them).`
                            : `${oos.length} stops are out of season on ${fmtDate(d.date)}${allOpen ? ` — all open from ${fmtDate(allOpen)}` : ""}. Move them to a later day, or drop them for now.`}
                        </p>
                      );
                    } else {
                      oos.forEach((s) => out.push(line(s)));
                    }
                    weekly.forEach((s) => out.push(line(s)));
                    return out;
                  })()}
                  {(d.timeline?.tight || []).map((w) => (
                    <p
                      key={w.id}
                      data-testid={`planner-day-tight-${d.day}-${w.id}`}
                      className="font-body font-700 text-[11px] text-[#B7791F] -mt-2 mb-4 flex items-center gap-1.5"
                    >
                      <AlertTriangle size={12} /> {w.name} closes {String(w.close).padStart(2, "0")}:00 — you'd get there ~{w.arrival}
                    </p>
                  ))}
                  {endsWithTivoli(d) && (
                    <div
                      data-testid={`planner-evening-tivoli-${d.day}`}
                      className="mb-4 bg-[#DF3226]/10 border border-[#DF3226]/30 rounded-xl px-3.5 py-2.5"
                    >
                      <p className="font-body font-800 text-xs text-black flex items-center gap-1.5">
                        <Clock size={13} className="text-[#DF3226] shrink-0" />
                        Ends with Tivoli Gardens in the evening — open late.
                      </p>
                      <p className="font-body text-xs text-black/65 mt-1">
                        Tivoli takes time: set aside 2–3h just for strolling, 3–5h with rides.
                      </p>
                    </div>
                  )}
                  {tipsForDay(d)
                    .filter((tip) => planTipDay[tip] === di)
                    .map((tip) => (
                    <div
                      key={tip}
                      data-testid={`planner-suggestion-${tip}`}
                      className="relative mb-4 bg-[#FFFCF0] border border-[#EACB12]/50 rounded-xl px-3.5 py-3 pr-9"
                    >
                      <button
                        type="button"
                        data-testid={`planner-suggestion-dismiss-${tip}`}
                        onClick={() => dismissTip(tip)}
                        aria-label="Dismiss this tip"
                        title="Dismiss"
                        className="absolute top-2 right-2 grid place-items-center w-6 h-6 rounded-full text-black/40 hover:text-white hover:bg-[#DF3226] transition-colors"
                      >
                        <X size={13} strokeWidth={2.5} />
                      </button>
                      <p className="font-body font-800 text-[10px] tracking-widest text-[#DF3226] mb-1 flex items-center gap-1">
                        <Lightbulb size={11} />
                        {tip === "market"
                          ? "MARKET DAY TIP"
                          : tip === "harbour"
                          ? "HARBOUR DAY TIP"
                          : "CHRISTIANSHAVN TIP"}
                      </p>
                      {tip === "market" ? (
                        marketHasSmorrebrod ? (
                          <>
                            <p className="font-body text-xs leading-snug">
                              Torvehallerne opens the day — and later on,{" "}
                              <span className="font-700">Sankt Peders Bageri</span> (CPH&apos;s oldest
                              bakery) sits right off the{" "}
                              <span className="font-700">Latin Quarter</span>, so it slots in next to
                              the stroll.
                            </p>
                            <div className="mt-2.5 flex flex-wrap gap-2">
                              {!selected.includes("latinquarter") && (
                                <button
                                  type="button"
                                  data-testid="planner-suggestion-add-latinquarter"
                                  onClick={() => addSuggested("latinquarter", di)}
                                  className="font-display font-800 text-xs bg-black text-white px-3 py-1.5 rounded-full hover:bg-[#DF3226] transition-colors"
                                >
                                  + Add Latin Quarter stroll
                                </button>
                              )}
                              {!selected.includes("sanktpeders") && (
                                <button
                                  type="button"
                                  data-testid="planner-suggestion-add-sanktpeders"
                                  onClick={() => addSuggested("sanktpeders", di)}
                                  className="font-display font-800 text-xs bg-white text-black px-3 py-1.5 rounded-full border border-zinc-200 hover:border-zinc-300 transition-colors"
                                >
                                  + Add Sankt Peders Bageri
                                </button>
                              )}
                            </div>
                          </>
                        ) : (
                          <p className="font-body text-xs leading-snug">
                            Torvehallerne opens at 10:00 — start with{" "}
                            <span className="font-700">breakfast inside the market</span>: porridge at
                            Grød, a flaky pastry and a proper coffee, then browse the stalls.
                          </p>
                        )
                      ) : tip === "harbour" ? (
                        <p className="font-body text-xs leading-snug">
                          Ease into the day with breakfast in the royal quarter —{" "}
                          <span className="font-700">Mormors café</span> (Bredgade 45),{" "}
                          <span className="font-700">Hart Bageri</span> (Store Kongensgade 75), or{" "}
                          <span className="font-700">Coffee Collective</span> — all a few minutes from
                          Amalienborg and Nyhavn.
                        </p>
                      ) : (
                        <p className="font-body text-xs leading-snug">
                          Start with breakfast by the canals:{" "}
                          <span className="font-700">The Packhouse</span> (Overgaden Neden Vandet 19A),{" "}
                          <span className="font-700">Parterre</span> (Overgaden Oven Vandet 90), or
                          porridge at <span className="font-700">Grød</span> (Prinsessegade 23).
                        </p>
                      )}
                    </div>
                  ))}
                  {areaTipsForDay(d)
                    .filter(
                      (t) =>
                        planAreaTipDay[t.key] === di &&
                        !tipsDismissed.includes(t.key) &&
                        // don't stack a second tip card on a day that already
                        // shows a breakfast/area tip (they overlap, e.g. both
                        // offering Sankt Peders Bageri)
                        !tipsForDay(d).some((tip) => planTipDay[tip] === di)
                    )
                    .map((t) => (
                      <div
                        key={t.key}
                        data-testid={`planner-area-tip-${t.key}`}
                        className="relative mb-4 bg-[#FFFCF0] border border-[#EACB12]/50 rounded-xl px-3.5 py-3 pr-9"
                      >
                        <button
                          type="button"
                          data-testid={`planner-area-tip-dismiss-${t.key}`}
                          onClick={() => dismissTip(t.key)}
                          aria-label="Dismiss this tip"
                          title="Dismiss"
                          className="absolute top-2 right-2 grid place-items-center w-6 h-6 rounded-full text-black/40 hover:text-white hover:bg-[#DF3226] transition-colors"
                        >
                          <X size={13} strokeWidth={2.5} />
                        </button>
                        <p className="font-body font-800 text-[10px] tracking-widest text-[#DF3226] mb-1 flex items-center gap-1">
                          <Lightbulb size={11} /> {t.kicker}
                        </p>
                        <p className="font-body text-xs leading-snug">{t.text}</p>
                        <div className="mt-2.5 flex flex-wrap gap-2">
                          {t.add.map((id, j) => (
                            <button
                              key={id}
                              type="button"
                              data-testid={`planner-area-tip-add-${id}`}
                              onClick={() => addSuggested(id, di)}
                              className={
                                j === 0
                                  ? "font-display font-800 text-xs bg-black text-white px-3 py-1.5 rounded-full hover:bg-[#DF3226] transition-colors"
                                  : "font-display font-800 text-xs bg-white text-black px-3 py-1.5 rounded-full border border-zinc-200 hover:border-zinc-300 transition-colors"
                              }
                            >
                              + Add {spotById(id)?.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  {showRoyalRescue && di === royalDayIdx && (
                    <div
                      data-testid="planner-royal-rescue"
                      className="relative mb-4 bg-[#FFFCF0] border border-[#EACB12]/50 rounded-xl px-3.5 py-3 pr-9"
                    >
                      <button
                        type="button"
                        data-testid="planner-royal-rescue-dismiss"
                        onClick={() => dismissTip("late-royals")}
                        aria-label="Dismiss this tip"
                        title="Dismiss"
                        className="absolute top-2 right-2 grid place-items-center w-6 h-6 rounded-full text-black/40 hover:text-white hover:bg-[#DF3226] transition-colors"
                      >
                        <X size={13} strokeWidth={2.5} />
                      </button>
                      <p className="font-body font-800 text-[10px] tracking-widest text-[#DF3226] mb-1 flex items-center gap-1">
                        <Lightbulb size={11} /> ARRIVING LATE?
                      </p>
                      <p className="font-body text-xs leading-snug">
                        Your late arrival left <span className="font-700">Amalienborg</span> &amp; the{" "}
                        <span className="font-700">Marble Church</span> off Day 1. They&apos;re a 15-min walk
                        and fit this morning nicely — add them here (we&apos;ll swap out Rundetårn to keep the
                        day balanced).
                      </p>
                      <div className="mt-2.5">
                        <button
                          type="button"
                          data-testid="planner-royal-rescue-add"
                          onClick={addRoyalsToOldTownDay}
                          className="font-display font-800 text-xs bg-black text-white px-3 py-1.5 rounded-full hover:bg-[#DF3226] transition-colors"
                        >
                          + Add to this morning
                        </button>
                      </div>
                    </div>
                  )}
                  {d.items.length > 0 && <DayMiniMap items={d.items} color="#DF3226" festive={festive} />}
                  {d.items.length ? (
                    <div className="space-y-2">
                      {d.timeline?.hotelOut && hotel && (
                        <HotelLeg leg={d.timeline.hotelOut} name={hotel.name} at={d.timeline.start} dayNum={d.day} />
                      )}
                      {d.items.map((s, si) => {
                        const leg = si < d.items.length - 1 ? travelBetween(s, d.items[si + 1]) : null;
                        // the "Getting there" card only makes sense on the day's
                        // FIRST stop — on a two-day-trip day the hop between them
                        // is already its own leg
                        const gt = si === 0 ? dayTripTransit(s) : null;
                        const isDragging = itemDrag && itemDrag.day === di && itemDrag.idx === si;
                        const isOver = itemDrag && itemDrag.day === di && itemDrag.overIdx === si && itemDrag.idx !== si;
                        return (
                          <Fragment key={s.id}>
                            <div
                              data-testid={`planner-stop-${d.day}-${s.id}`}
                              draggable
                              onDragStart={(e) => { e.stopPropagation(); setItemDrag({ day: di, idx: si, overIdx: si }); }}
                              onDragEnd={(e) => { e.stopPropagation(); setItemDrag(null); }}
                              onDragOver={(e) => {
                                if (!itemDrag || itemDrag.day !== di) return;
                                e.preventDefault();
                                e.stopPropagation();
                                if (itemDrag.overIdx !== si) setItemDrag((p) => ({ ...p, overIdx: si }));
                              }}
                              onDrop={(e) => {
                                if (!itemDrag || itemDrag.day !== di) return;
                                e.stopPropagation();
                                moveItem(di, itemDrag.idx, si);
                                setItemDrag(null);
                              }}
                              className={`group/stop flex gap-2 items-start -mx-1 px-1 py-1 rounded-lg transition-all ${
                                isDragging ? "opacity-40" : ""
                              } ${isOver ? "bg-black/5 ring-1 ring-black/30" : ""}`}
                            >
                              <span
                                aria-hidden="true"
                                title="Drag to reorder"
                                className="shrink-0 mt-0.5 text-black/30 group-hover/stop:text-black/50 cursor-grab active:cursor-grabbing transition-colors"
                              >
                                <GripVertical size={14} />
                              </span>
                              <span className="font-display font-900 text-xs text-black/70 w-11 shrink-0 mt-0.5">
                                {d.timeline?.at?.[si] ? (
                                  <>
                                    <span data-testid={`planner-stop-at-${d.day}-${s.id}`} className="block">{d.timeline.at[si]}</span>
                                    <span className="block font-body font-700 text-[10px] text-black/45 -mt-0.5">{spotDur(s)}</span>
                                  </>
                                ) : (
                                  spotDur(s)
                                )}
                              </span>
                              <span className="font-body text-sm leading-snug flex-1">
                                {s.gyg ? (
                                  <a
                                    href={s.gyg}
                                    target="_blank"
                                    rel="noopener noreferrer sponsored"
                                    data-testid={`planner-gyg-${s.id}`}
                                    title={`Tickets & tours for ${s.name}`}
                                    className="font-700 underline decoration-[#DF3226]/60 underline-offset-2 hover:text-[#DF3226] transition-colors"
                                  >
                                    {s.name}
                                  </a>
                                ) : (
                                  <span className="font-700">{s.name}</span>
                                )}
                                {s.note && <span className="block text-xs text-black/65 italic">{s.note}</span>}
                                {(() => {
                                  const h = (d.skip || []).find((x) => x.id === s.id);
                                  return h ? (
                                    <span
                                      data-testid={`planner-skip-hint-${d.day}-${s.id}`}
                                      className="flex items-start gap-1 mt-1 text-xs font-body font-700 text-[#E8590C]"
                                    >
                                      <SkipForward size={12} className="shrink-0 mt-0.5" /> {h.text}
                                    </span>
                                  ) : null;
                                })()}
                                {d.timeline?.lunchCovered?.id === s.id && s.id !== "smorrebrod" && (
                                  <span
                                    data-testid={`planner-lunch-covered-${d.day}-${s.id}`}
                                    className="flex items-start gap-1 mt-1 text-xs font-body font-700 text-[#B8860B]"
                                  >
                                    <Utensils size={12} className="shrink-0 mt-0.5" /> Eat lunch at {d.timeline.lunchCovered.name}
                                  </span>
                                )}
                                {s.page && (
                                  <Link
                                    to={s.page}
                                    data-testid={`planner-trip-link-${s.id}`}
                                    className="inline-flex items-center gap-1 mt-0.5 font-body font-700 text-[11px] text-[#DF3226] underline underline-offset-2 decoration-[#DF3226]/40 hover:decoration-[#DF3226] transition-colors"
                                  >
                                    Day trip guide <ChevronRight size={12} />
                                  </Link>
                                )}
                                {gt && (
                                  <span className="mt-1 flex items-start gap-1.5 text-[11px] font-700 text-black/65">
                                    <TrainFront size={12} className="shrink-0 mt-0.5" />
                                    <span>Getting there · ~{gt.min} min {gt.label}{gt.note ? ` — ${gt.note}` : ""}</span>
                                  </span>
                                )}
                              </span>
                              <span className="flex items-center gap-0.5 shrink-0 opacity-70 md:opacity-0 md:group-hover/stop:opacity-100 focus-within:opacity-100 transition-opacity">
                                <button
                                  data-testid={`planner-stop-up-${d.day}-${s.id}`}
                                  onClick={() => moveItem(di, si, si - 1)}
                                  disabled={si === 0}
                                  aria-label={`Move ${s.name} earlier`}
                                  title="Move earlier"
                                  className="w-6 h-6 flex items-center justify-center rounded-full text-black/40 hover:text-black hover:bg-[#F1EDE3] disabled:opacity-20 disabled:hover:bg-transparent transition-colors"
                                >
                                  <ChevronUp size={14} />
                                </button>
                                <button
                                  data-testid={`planner-stop-down-${d.day}-${s.id}`}
                                  onClick={() => moveItem(di, si, si + 1)}
                                  disabled={si === d.items.length - 1}
                                  aria-label={`Move ${s.name} later`}
                                  title="Move later"
                                  className="w-6 h-6 flex items-center justify-center rounded-full text-black/40 hover:text-black hover:bg-[#F1EDE3] disabled:opacity-20 disabled:hover:bg-transparent transition-colors"
                                >
                                  <ChevronDown size={14} />
                                </button>
                                <button
                                  data-testid={`planner-remove-${d.day}-${s.id}`}
                                  onClick={() => removeItem(d.day, s.id)}
                                  aria-label={`Remove ${s.name}`}
                                  title="Remove from this day"
                                  className="w-6 h-6 flex items-center justify-center rounded-full text-black/40 hover:text-white hover:bg-[#DF3226] transition-colors"
                                >
                                  <X size={14} />
                                </button>
                              </span>
                            </div>
                            {leg && (
                              <div
                                data-testid={`planner-leg-${d.day}-${si}`}
                                className="flex items-center gap-1.5 pl-[4.9rem] text-[11px] font-700 text-black/60"
                              >
                                <CornerDownRight size={12} className="shrink-0" />
                                <span className="opacity-70">then</span>
                                {leg.mode === "walk" ? <Footprints size={12} /> : <TrainFront size={12} />}
                                ~{leg.min} min {leg.label}
                                {leg.note && <span className="italic font-600 opacity-80">· {leg.note}</span>}
                              </div>
                            )}
                            {d.timeline?.lunch?.afterIdx === si &&
                              (mealShown("lunch", d) ? (
                                <MealRow
                                  kind="lunch"
                                  at={d.timeline.lunch.at}
                                  venues={d.timeline.lunch.venues}
                                  quick={d.timeline.lunch.quick}
                                  dayNum={d.day}
                                  onDismiss={() => dismissMeal("lunch", d)}
                                  onMove={(delta) => moveLunch(di, delta)}
                                  canEarlier={si > 0}
                                  canLater={si < d.items.length - 1}
                                />
                              ) : (
                                <button
                                  type="button"
                                  data-testid={`planner-lunch-restore-${d.day}`}
                                  onClick={() => restoreMeal("lunch", d)}
                                  title="Show the lunch break again"
                                  className="flex items-center gap-1.5 -mx-1 px-1 py-1.5 text-[11px] font-body font-700 text-black/40 hover:text-[#DF3226] transition-colors"
                                >
                                  <span className="w-[14px] shrink-0" />
                                  <Plus size={12} className="shrink-0" /> Show lunch break
                                </button>
                              ))}
                          </Fragment>
                        );
                      })}
                      {d.timeline?.dinner && (d.timeline.dinner.picked || mealShown("dinner", d)) && (
                        <MealRow
                          kind="dinner"
                          at={d.timeline.dinner.at}
                          venues={d.timeline.dinner.venues}
                          picked={d.timeline.dinner.picked}
                          dayNum={d.day}
                          onPick={(v) => setDinner(di, v)}
                          onDismiss={() => (d.timeline.dinner.picked ? setDinner(di, null) : dismissMeal("dinner", d))}
                        />
                      )}
                      {d.timeline?.hotelBack && hotel && (
                        <HotelLeg leg={d.timeline.hotelBack} name={hotel.name} at={d.timeline.end.replace("~", "")} back dayNum={d.day} />
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-black/50 py-4">
                      <Coffee size={18} />
                      <span className="font-body text-sm">A free day — wander, rest, or take an idea:</span>
                    </div>
                  )}
                  {/* ideas for a free or light day — big dense areas (centre +
                      harbour) keep offering ideas until the day reaches ~5h,
                      smaller areas stop at ~3h; on a day with stops only CLOSE
                      spots are offered, nearest first */}
                  {(() => {
                    // Tivoli-only day: you eat inside the gardens (no lunch
                    // break), so there's room for something small nearby. Tivoli's
                    // packed hours would normally hit the day limit and hide the
                    // ideas block, so offer these light suggestions on their own.
                    const tivoliOnly =
                      d.items.length > 0 &&
                      d.items.some((s) => s.id === "tivoli") &&
                      d.items.every((s) => s.id === "tivoli" || TIVOLI_DAY_EXTRAS.includes(s.id));
                    if (tivoliOnly) {
                      const extras = TIVOLI_DAY_EXTRAS.filter((id) => {
                        const s = SPOTS.find((x) => x.id === id);
                        return s && !selected.includes(id) && avail(s);
                      });
                      if (!extras.length) return null;
                      return (
                        <div
                          data-testid={`planner-tivoli-extras-${d.day}`}
                          className="mt-4 pt-4 border-t border-zinc-100"
                        >
                          <p className="font-body text-xs text-black/50 mb-3">
                            You&apos;ll eat inside Tivoli — but there&apos;s room for something small nearby:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {extras.map((id) => {
                              const s = SPOTS.find((x) => x.id === id);
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  data-testid={`planner-free-idea-${id}`}
                                  onClick={() => addSuggested(id, di)}
                                  className="inline-flex items-center gap-1.5 font-body font-700 text-xs bg-white text-black/80 px-3 py-1.5 rounded-full border border-zinc-300 hover:border-black hover:text-black transition-colors"
                                >
                                  <Plus size={12} className="text-[#DF3226]" /> {s.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    const bigArea = d.items.some((s) => s.cluster === "indre-by" || s.cluster === "harbour");
                    const dayLimit = bigArea ? 5 : 3;
                    if (d.hours >= dayLimit) return null;
                    const fresh = (ids) =>
                      ids.filter((id) => {
                        const s = SPOTS.find((x) => x.id === id);
                        return s && !selected.includes(id) && avail(s);
                      });
                    // rank by closeness to this day's stops (or the whole plan
                    // when the day is empty) so suggestions fit the trip
                    const refs = d.items.length ? d.items : (plan || []).flatMap((x) => x.items);
                    const d2 = (a, b) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
                    const nearestToRefs = (spot) => Math.min(...refs.map((r) => d2(spot.coords, r.coords)));
                    const rank = (ids) =>
                      !refs.length
                        ? ids
                        : [...ids].sort((x, y) => {
                            const sx = SPOTS.find((s) => s.id === x);
                            const sy = SPOTS.find((s) => s.id === y);
                            return nearestToRefs(sx) - nearestToRefs(sy);
                          });
                    // on a day that already has stops, only suggest a spot if THIS
                    // day is its nearest day — the Designmuseum belongs to the
                    // Nyhavn day's card, not the Torvehallerne day's
                    const isNearestDay = (id) => {
                      if (!d.items.length) return true;
                      const s = SPOTS.find((x) => x.id === id);
                      const here = Math.min(...d.items.map((r) => d2(s.coords, r.coords)));
                      return (plan || []).every(
                        (other) =>
                          other.day === d.day ||
                          !other.items.length ||
                          here <= Math.min(...other.items.map((r) => d2(s.coords, r.coords))) + 1e-9
                      );
                    };
                    const whole = rank(fresh(FREE_DAY_IDEAS.whole));
                    let couple;
                    if (d.items.length) {
                      // days with stops: any unselected, season-ok spot within
                      // ~1.5 km of the day's stops — near ideas only, max 5
                      const NEAR2 = 0.014 * 0.014;
                      // when this day would host the newcomer: the clock it
                      // would start at (the stop it slots before, or the day's
                      // end if it lands last). Keeps early-closing ideas
                      // (museums) off a day that already runs late.
                      const parseClock = (c) => { const [h, m] = c.split(":").map(Number); return h + m / 60; };
                      const wouldStartAt = (spot) => {
                        const k = insertionIndexFor(d.items, spot);
                        return k >= d.items.length || !d.timeline?.at?.[k]
                          ? (d.timeline?.endH ?? 10)
                          : parseClock(d.timeline.at[k]);
                      };
                      couple = rank(
                        fresh(
                          SPOTS.filter((s) => s.cluster !== "daytrip" && s.cluster !== "partners" && !s.event).map(
                            (s) => s.id
                          )
                        ).filter((id) => nearestToRefs(SPOTS.find((x) => x.id === id)) <= NEAR2)
                      )
                        .filter(isNearestDay)
                        .filter((id) => fitsBeforeClose(spotById(id), wouldStartAt(spotById(id)), month))
                        .slice(0, 5);
                    } else {
                      couple = rank(fresh(FREE_DAY_IDEAS.hours));
                    }
                    // Tivoli is always a good idea on a light centre/harbour
                    // day — it's the close-by evening filler there (it would be
                    // a cross-town trek on a neighbourhood day, so skip those)
                    const tivoliOk =
                      d.items.length > 0 &&
                      bigArea &&
                      d.hours <= dayLimit &&
                      !selected.includes("tivoli") &&
                      avail(SPOTS.find((x) => x.id === "tivoli"));
                    // whole-day ideas only render on an empty day — don't let them
                    // keep the block alive on a day that already has stops
                    const showWhole = whole.length > 0 && !d.items.length;
                    if (!showWhole && !couple.length && !tivoliOk) return null;
                    const IdeaBtn = ({ id }) => {
                      const s = SPOTS.find((x) => x.id === id);
                      return (
                        <button
                          type="button"
                          data-testid={`planner-free-idea-${id}`}
                          onClick={() => addSuggested(id, di)}
                          className="inline-flex items-center gap-1.5 font-body font-700 text-xs bg-white text-black/80 px-3 py-1.5 rounded-full border border-zinc-300 hover:border-black hover:text-black transition-colors"
                        >
                          <Plus size={12} className="text-[#DF3226]" /> {s.name}
                        </button>
                      );
                    };
                    return (
                      <div data-testid={`planner-free-day-${d.day}`} className={d.items.length ? "mt-4 pt-4 border-t border-zinc-100" : "pb-2"}>
                        {d.items.length > 0 && (couple.length > 0 || tivoliOk) && (
                          <p className="font-body text-xs text-black/50 mb-3">Room for more — ideas that fit:</p>
                        )}
                        {showWhole && (
                          <>
                            <p className="font-body font-800 text-[10px] tracking-[0.2em] text-black/45 mb-1.5">
                              A WHOLE DAY COULD BE…
                            </p>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {whole.map((id) => (
                                <IdeaBtn key={id} id={id} />
                              ))}
                            </div>
                          </>
                        )}
                        {(couple.length > 0 || tivoliOk) && (
                          <>
                            <p className="font-body font-800 text-[10px] tracking-[0.2em] text-black/45 mb-1.5">
                              A COUPLE OF HOURS…
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {tivoliOk && <IdeaBtn id="tivoli" />}
                              {couple.map((id) => (
                                <IdeaBtn key={id} id={id} />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  {d.items.some((s) => s.coords) && (
                    <a
                      data-testid={`planner-navigate-${d.day}`}
                      href={dayMapsUrl(d.items)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-4 self-start inline-flex items-center gap-2 font-body font-800 text-sm bg-black text-white px-4 py-2.5 rounded-full hover:bg-[#DF3226] transition-colors"
                    >
                      <Navigation size={15} /> Navigate this day
                    </a>
                  )}
                  {daytimeHours(d) > 7 && (
                    <span className="mt-4 self-start font-body font-800 text-[11px] bg-[#DF3226] text-white px-2.5 py-1 border border-zinc-200">
                      BUSY DAY — maybe trim one
                    </span>
                  )}
                </motion.div>
              ))}
              </div>
              {tripValid?.departureMorning && (
                <div className="mt-5">
                  <DepartureMorningCard date={tripValid.departureMorning} by={tripDates.leaveBy} hotel={hotel} />
                </div>
              )}
              <BookAhead plan={plan} />

              {/* month note + estimated opening times — reference info, so it
                  sits under the day cards, right before print/share */}
              {(() => {
                const info = month !== "any" ? MONTH_INFO[month] : null;
                const event = month !== "any" ? MONTH_EVENTS[month] : null;
                const tip = month !== "any" ? MONTH_TIPS[month] : null;
                const openNotes = openingNotes(selected.map((id) => spotById(id)));
                if (!info && !openNotes.length) return null;
                const monthName = MONTHS.find((x) => x.id === month)?.full;
                const solo = !info; // no month note — the box is only the opening-times fold-out
                const notesList = (
                  <ul className="mt-2 space-y-1.5">
                    {openNotes.map((n) => (
                      <li key={n.open} className={`font-body text-xs leading-snug ${solo ? "text-white/90" : "text-black/70"}`}>
                        {n.anon ? n.open : <><span className={`font-700 ${solo ? "text-white" : "text-black/85"}`}>{n.name}:</span> {n.open}</>}
                        {n.tip && (
                          <span className="mt-1 flex items-start gap-1.5 bg-[#FDF3C7] text-black/70 border border-[#EACB12]/40 px-2 py-1 rounded-md font-editorial italic text-[11px] leading-snug">
                            <Lightbulb size={11} className="shrink-0 mt-0.5 text-[#B8860B]" /> {n.tip}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                );
                return (
                  <div
                    data-testid="planner-season-notes"
                    className={
                      solo
                        ? "mt-6 bg-[#DF3226] text-white border border-black shadow-brut rounded-xl px-4 py-2.5"
                        : "mt-6 bg-[#FCEFC5] text-black border border-black shadow-brut rounded-xl px-4 py-3.5 md:p-6"
                    }
                  >
                    {info && (
                      <div data-testid="planner-typical-weather">
                        <p className="font-body text-[13px] md:text-sm leading-snug text-black/75">
                          <span className="font-display font-900 text-sm md:text-base text-black">
                            Typical for {monthName}: {info.temp}
                          </span>
                          <span className="block mt-1.5">{info.note}</span>
                        </p>
                        <p className="mt-1.5 flex items-start gap-1.5 font-body text-[13px] md:text-sm leading-snug text-black/75">
                          <Luggage size={14} className="shrink-0 mt-0.5 text-black/60" />
                          <span>
                            <span className="font-800 text-black">Pack:</span> {info.pack}
                          </span>
                        </p>
                      </div>
                    )}
                    {event && (
                      <p className="font-body text-xs md:text-[13px] mt-1.5 inline-flex items-start gap-1.5 text-black/70">
                        <Mark size={13} className="shrink-0 mt-0.5" /> {event}
                      </p>
                    )}
                    {tip && (
                      <p className="font-editorial italic text-xs md:text-[13px] mt-1 text-black/60">
                        *Pro tip: {tip}
                      </p>
                    )}
                    {(month === 11 || month === 12) && (
                      <p className="mt-2">
                        <Link
                          to="/christmas"
                          data-testid="planner-christmas-link"
                          className="inline-flex items-center gap-1.5 font-body font-800 text-[13px] md:text-sm text-[#DF3226] underline underline-offset-4 hover:text-black transition-colors"
                        >
                          Christmas in Copenhagen — markets, gløgg & lights <ChevronRight size={14} />
                        </Link>
                      </p>
                    )}
                    {openNotes.length > 0 && openNotes.length <= 3 && (
                      // 1–3 notes: just show them — folding two lines away helps no one
                      <div className={solo ? "" : "mt-2.5 pt-2.5 border-t border-black/10"}>
                        <p
                          data-testid="planner-opening-times-toggle"
                          className={`font-body font-800 text-[11px] tracking-widest uppercase ${
                            solo ? "text-white" : "text-[#DF3226]"
                          }`}
                        >
                          Estimated opening & tips
                        </p>
                        {notesList}
                      </div>
                    )}
                    {openNotes.length > 3 && (
                      <details className={solo ? "group" : "mt-2.5 pt-2.5 border-t border-black/10 group"}>
                        <summary
                          data-testid="planner-opening-times-toggle"
                          className={`cursor-pointer list-none flex items-center gap-1 font-body font-800 text-[11px] tracking-widest uppercase transition-colors ${
                            solo ? "text-white hover:text-white/80" : "text-[#DF3226] hover:text-black"
                          }`}
                        >
                          <ChevronRight size={12} className="transition-transform group-open:rotate-90" />
                          Estimated opening & tips ({openNotes.length})
                        </summary>
                        {notesList}
                      </details>
                    )}
                  </div>
                );
              })()}

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  data-testid="planner-print"
                  onClick={handlePrint}
                  className="font-display font-800 text-base rounded-2xl bg-white text-black border border-black px-6 py-3.5 inline-flex items-center gap-2.5 shadow-brut-sm hover:bg-black hover:text-white transition-all duration-200"
                >
                  <Printer size={18} /> Print / PDF
                </button>
                <button
                  data-testid="planner-qr"
                  onClick={handleShowQR}
                  aria-pressed={showQR}
                  className={`font-display font-800 text-base rounded-2xl px-6 py-3.5 border inline-flex items-center gap-2.5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                    showQR
                      ? "bg-black text-white border-black"
                      : "bg-white text-black border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <QrCode size={18} /> QR
                </button>
                <button
                  data-testid="planner-story"
                  onClick={handleStory}
                  className="font-display font-800 text-base rounded-2xl bg-[#DF3226] text-white px-6 py-3.5 inline-flex items-center gap-2.5 shadow-brut-sm hover:bg-black transition-all duration-200"
                >
                  <Instagram size={18} /> Story
                </button>
                <button
                  data-testid="planner-trip-map"
                  onClick={handleTripMap}
                  className="font-display font-800 text-base rounded-2xl bg-white text-black px-6 py-3.5 border border-zinc-200 inline-flex items-center gap-2.5 shadow-sm hover:shadow-lg hover:border-zinc-300 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                >
                  <MapIcon size={18} /> Trip map
                </button>
                <button
                  data-testid="planner-share"
                  onClick={handleShare}
                  className={`font-display font-800 text-base rounded-2xl px-6 py-3.5 inline-flex items-center gap-2.5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                    copied
                      ? "bg-[#0E7C5A] text-white"
                      : "bg-black text-white hover:bg-[#DF3226]"
                  }`}
                >
                  {copied ? <Check size={18} /> : <Share2 size={18} />} {copied ? "Copied!" : "Share"}
                </button>
              </div>

              <AnimatePresence>
                {showQR && qrUrl && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    data-testid="planner-qr-panel"
                    className="mt-4 inline-flex items-center gap-5 bg-white border border-zinc-200 shadow-brut p-5"
                  >
                    <div className="bg-white p-2 border border-zinc-200 text-center">
                      <QRCodeCanvas value={qrUrl} size={116} level="M" includeMargin={false} />
                      <p data-testid="planner-qr-brand" className="font-body font-800 text-[10px] tracking-widest text-black/70 mt-1.5">
                        CPHHIGHLIGHTS.COM
                      </p>
                    </div>
                    <div className="max-w-[240px]">
                      <p className="font-display font-900 tracking-tight text-lg leading-none">
                        Scan to open on your phone
                      </p>
                      <p className="font-body text-sm text-black/60 mt-2 leading-relaxed">
                        Point your camera at the code to carry this plan with you as you explore.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <p
                data-testid="planner-disclaimer"
                className="mt-5 flex items-start gap-1.5 font-body text-xs text-black/50 max-w-xl leading-relaxed"
              >
                <Info size={13} className="shrink-0 mt-0.5" />
                <span>
                  Times and routing are local estimates — always check official opening hours before
                  you go.{" "}
                  <Link to="/terms" className="underline underline-offset-2 hover:text-black transition-colors">
                    Read the terms
                  </Link>
                  .
                </span>
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
